import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import AssignmentViewer from '../components/AssignmentViewer';
import CriticalErrorPopup from '../components/CriticalErrorPopup';
import ProjectComments from '../components/ProjectComments';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { getValidEnrollment } from '../utils/enrollmentUtils';
import { v4 as uuidv4 } from 'uuid';
import './CourseViewer.css';

// Component to display user's responses to main post with hierarchical replies
const UserMainPostResponses = ({ referenceId, userId, showAllComments = false, discussionData, onCommentReplyClick, onCancelCommentReply, onSubmitCommentReply, showCommentReplyForm, commentReplyContent, setCommentReplyContent, submittingCommentReply }) => {
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadResponses = async () => {
      if (!referenceId || !userId) {
        setResponses([]);
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/comments?reference_id=${referenceId}&table=discussionboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Organize comments hierarchically
        const allComments = response.data.comments;
        const organizedComments = organizeCommentsHierarchically(allComments);
        
        let filteredComments;
        if (showAllComments) {
          // Show all top-level comments (responses to main post)
          filteredComments = organizedComments.filter((c) => c.reference_id === c.parent);
        } else {
          // Filter to user's responses to main post only
          filteredComments = organizedComments.filter((c) => 
            c.account === userId && (c.reference_id === c.parent)
          );
        }
        setResponses(filteredComments);
      } catch (error) {
        console.error('[CourseViewer] Error loading user responses:', error);
        setResponses([]);
      } finally {
        setLoading(false);
      }
    };

    loadResponses();

    // Listen for refresh events
    const handleRefresh = () => {
      loadResponses();
    };

    window.addEventListener('refreshDiscussionResponses', handleRefresh);

    return () => {
      window.removeEventListener('refreshDiscussionResponses', handleRefresh);
    };
  }, [referenceId, userId, showAllComments]);

  const organizeCommentsHierarchically = (allComments) => {
    const commentMap = new Map();
    const rootComments = [];
    
    // First pass: create a map of all comments by ID
    allComments.forEach(comment => {
      commentMap.set(comment.id, { ...comment, replies: [] });
    });
    
    // Second pass: organize into hierarchy
    allComments.forEach(comment => {
      if (comment.parent && comment.parent !== comment.reference_id) {
        // This is a reply to another comment
        const parentComment = commentMap.get(comment.parent);
        if (parentComment) {
          parentComment.replies.push(commentMap.get(comment.id));
        }
      } else {
        // This is a top-level comment (reply to main post)
        rootComments.push(commentMap.get(comment.id));
      }
    });
    
    return rootComments;
  };

  const renderCommentWithReplies = (comment, depth = 0) => {
    const isUserComment = comment.account === userId;
    return (
      <div key={comment.id} style={{
        padding: '0.75rem',
        backgroundColor: isUserComment ? '#e3f2fd' : 'white',
        borderRadius: '6px',
        border: isUserComment ? '1px solid #bbdefb' : '1px solid #e5e7eb',
        marginBottom: depth === 0 ? '0.5rem' : '0.25rem',
        marginLeft: depth > 0 ? `${depth * 20}px` : '0'
      }}>
        <div style={{ 
          fontSize: '12px', 
          color: '#64748b', 
          marginBottom: '0.5rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span style={{ fontWeight: '500' }}>
            {comment.first_name} {comment.last_name}
          </span>
          <span>
            {new Date(comment.createddate).toLocaleDateString()} {new Date(comment.createddate).toLocaleTimeString()}
          </span>
        </div>
        <div 
          style={{ 
            fontSize: '14px', 
            color: '#374151',
            lineHeight: '1.5'
          }}
          dangerouslySetInnerHTML={{ __html: comment.content }}
        />
        
        {/* Reply button for comments */}
        {discussionData && discussionData.allowcommentresponse === true && (
          <div style={{ marginTop: '0.75rem', paddingTop: '0.5rem', borderTop: '1px solid #f3f4f6' }}>
            <button
              onClick={() => onCommentReplyClick(comment.id)}
              style={{
                padding: '0.375rem 0.75rem',
                backgroundColor: 'transparent',
                color: '#6b7280',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.75rem',
                fontWeight: '400',
                transition: 'all 0.2s'
              }}
            >
              💬 Reply
            </button>
          </div>
        )}
        
        {/* Reply form for this comment */}
        {showCommentReplyForm === comment.id && (
          <div style={{ 
            marginTop: '1rem', 
            padding: '1rem', 
            backgroundColor: '#f9fafb', 
            borderRadius: '8px', 
            border: '1px solid #e5e7eb',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{ 
              marginBottom: '1rem',
              position: 'relative',
              zIndex: 1
            }}>
              <ReactQuill
                value={commentReplyContent}
                onChange={setCommentReplyContent}
                modules={quillModules}
                placeholder="Write your reply..."
                style={{ height: '120px' }}
              />
            </div>
            <div style={{ 
              display: 'flex', 
              gap: '0.75rem', 
              justifyContent: 'flex-end',
              position: 'relative',
              zIndex: 3,
              marginTop: '1rem'
            }}>
              <button
                onClick={() => onSubmitCommentReply(comment.id)}
                disabled={submittingCommentReply || !commentReplyContent.trim()}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: submittingCommentReply || !commentReplyContent.trim() ? '#9ca3af' : '#2563eb',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  transition: 'background-color 0.2s'
                }}
              >
                {submittingCommentReply ? 'Submitting...' : 'Submit Reply'}
              </button>
              <button
                onClick={onCancelCommentReply}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  transition: 'background-color 0.2s'
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
        
        {/* Render nested replies */}
        {comment.replies && comment.replies.length > 0 && (
          <div style={{ marginTop: '1rem', paddingLeft: '1rem', borderLeft: '2px solid #e5e7eb' }}>
            {comment.replies.map(reply => renderCommentWithReplies(reply, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <div style={{ fontSize: '14px', color: '#64748b' }}>Loading responses...</div>;
  }

  if (responses.length === 0) {
    return <div style={{ fontSize: '14px', color: '#64748b', fontStyle: 'italic' }}>
      {showAllComments ? 'No responses yet' : 'No responses yet'}
    </div>;
  }

  return (
    <div>
      {responses.map((response, index) => renderCommentWithReplies(response))}
    </div>
  );
};

// Component to display response permissions info with conditional collapse
const ResponsePermissionsInfo = ({ discussionData, currentComponent, userData }) => {
  const [userResponses, setUserResponses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUserResponses = async () => {
      if (!currentComponent?.reference_id || !userData?.account_id) {
        setUserResponses([]);
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/comments?reference_id=${currentComponent.reference_id}&table=discussionboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Filter to user's responses to main post
        const userResponsesToMainPost = response.data.comments.filter((c) => 
          c.account === userData.account_id && (c.reference_id === c.parent)
        );
        setUserResponses(userResponsesToMainPost);
      } catch (error) {
        console.error('[CourseViewer] Error loading user responses for permissions:', error);
        setUserResponses([]);
      } finally {
        setLoading(false);
      }
    };

    loadUserResponses();
  }, [currentComponent?.reference_id, userData?.account_id]);

  // Don't show if loading
  if (loading) {
    return null;
  }

  const canViewOthers = discussionData.allowassigneesviewresponses === true;
  const hideUntilResponded = discussionData.hideresponsesuntilresponded === true;
  const hasRespondedToMainPost = userResponses.length > 0;

  // Collapse only when peer viewing is on, staggered reveal is on, and the student has responded (no banner needed)
  if (canViewOthers && hideUntilResponded && hasRespondedToMainPost) {
    return null;
  }

  // If canViewOthers is false, always show the restriction (hide-until has no effect on visibility here)
  if (!canViewOthers) {
    return (
      <div style={{ marginTop: '1rem', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
        <div style={{ fontSize: '13px', color: '#666', fontWeight: '500' }}>
          You may not see others' responses
        </div>
      </div>
    );
  }

  // If hideUntilResponded is true and user hasn't responded, show restriction
  if (hideUntilResponded && !hasRespondedToMainPost) {
    return (
      <div style={{ marginTop: '1rem', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
        <div style={{ fontSize: '13px', color: '#666', fontWeight: '500' }}>
          You may not view others' responses until you respond to the main post
        </div>
      </div>
    );
  }

  // If all conditions are met, show permission
  return (
    <div style={{ marginTop: '1rem', padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
      <div style={{ fontSize: '13px', color: '#666', fontWeight: '500' }}>
        You may view others' responses regardless if you respond
      </div>
    </div>
  );
};

// Component to display comment statistics
const CommentStatistics = ({ referenceId, userId, showAllComments }) => {
  const [stats, setStats] = useState({
    totalComments: 0,
    mainPostReplies: 0,
    commentReplies: 0,
    userMainReplies: 0,
    userCommentReplies: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      if (!referenceId || !userId) {
        setStats({
          totalComments: 0,
          mainPostReplies: 0,
          commentReplies: 0,
          userMainReplies: 0,
          userCommentReplies: 0
        });
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/comments?reference_id=${referenceId}&table=discussionboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const allComments = response.data.comments;
        
        // Calculate statistics
        const mainPostReplies = allComments.filter(c => c.reference_id === c.parent);
        const commentReplies = allComments.filter(c => c.parent !== c.reference_id);
        const userMainReplies = mainPostReplies.filter(c => c.account === userId);
        const userCommentReplies = commentReplies.filter(c => c.account === userId);
        
        setStats({
          totalComments: allComments.length,
          mainPostReplies: mainPostReplies.length,
          commentReplies: commentReplies.length,
          userMainReplies: userMainReplies.length,
          userCommentReplies: userCommentReplies.length
        });
      } catch (error) {
        console.error('[CourseViewer] Error loading comment statistics:', error);
        setStats({
          totalComments: 0,
          mainPostReplies: 0,
          commentReplies: 0,
          userMainReplies: 0,
          userCommentReplies: 0
        });
      } finally {
        setLoading(false);
      }
    };

    loadStats();

    // Listen for refresh events
    const handleRefresh = () => {
      loadStats();
    };

    window.addEventListener('refreshDiscussionResponses', handleRefresh);

    return () => {
      window.removeEventListener('refreshDiscussionResponses', handleRefresh);
    };
  }, [referenceId, userId]);

  if (loading) {
    return <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '1rem' }}>Loading comment statistics...</div>;
  }

  if (stats.totalComments === 0) {
    return null; // Don't show anything if no comments
  }

  return (
    <div style={{ 
      fontSize: '14px', 
      color: '#64748b', 
      marginBottom: '1rem',
      padding: '0.75rem',
      backgroundColor: '#f8f9fa',
      borderRadius: '6px',
      border: '1px solid #e9ecef'
    }}>
      {(stats.userMainReplies > 0 || stats.userCommentReplies > 0) && (
        <div style={{ fontSize: '15px', fontWeight: '600', color: '#495057', marginBottom: '0.5rem' }}>
          You have {stats.userMainReplies} comment{stats.userMainReplies !== 1 ? 's' : ''} to post and {stats.userCommentReplies} comment{stats.userCommentReplies !== 1 ? 's' : ''} to students
        </div>
      )}
      <div style={{ fontWeight: '500' }}>
        {stats.totalComments} comment{stats.totalComments !== 1 ? 's' : ''} to this post ({stats.mainPostReplies} to main, {stats.commentReplies} to other comments)
      </div>
    </div>
  );
};

// Component to display discussion responses (all or user's only)
const DiscussionResponses = ({ discussionData, currentComponent, userData, onCommentReplyClick, onCancelCommentReply, onSubmitCommentReply, showCommentReplyForm, commentReplyContent, setCommentReplyContent, submittingCommentReply }) => {
  const [userResponses, setUserResponses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUserResponses = async () => {
      if (!currentComponent?.reference_id || !userData?.account_id) {
        setUserResponses([]);
        setLoading(false);
        return;
      }

      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/comments?reference_id=${currentComponent.reference_id}&table=discussionboard`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Filter to user's responses to main post
        const userResponsesToMainPost = response.data.comments.filter((c) => 
          c.account === userData.account_id && (c.reference_id === c.parent)
        );
        setUserResponses(userResponsesToMainPost);
      } catch (error) {
        console.error('[CourseViewer] Error loading user responses for discussion:', error);
        setUserResponses([]);
      } finally {
        setLoading(false);
      }
    };

    loadUserResponses();
  }, [currentComponent?.reference_id, userData?.account_id]);

  if (loading) {
    return null;
  }

  const canViewOthers = discussionData.allowassigneesviewresponses === true;
  const hideUntilResponded = discussionData.hideresponsesuntilresponded === true;
  const hasRespondedToMainPost = userResponses.length > 0;

  // Determine what to show
  const showAllComments = canViewOthers && (!hideUntilResponded || hasRespondedToMainPost);
  const headerText = showAllComments ? "All Responses (blue responses are yours):" : "Your Response(s) to Main Post:";

  return (
    <div style={{ 
      marginTop: '1rem'
    }}>
      <div style={{ 
        fontSize: '14px', 
        fontWeight: '600', 
        color: '#0369a1', 
        marginBottom: '0.75rem' 
      }}>
        {headerText}
      </div>
      <UserMainPostResponses 
        referenceId={currentComponent.reference_id}
        userId={userData.account_id}
        showAllComments={showAllComments}
        discussionData={discussionData}
        onCommentReplyClick={onCommentReplyClick}
        onCancelCommentReply={onCancelCommentReply}
        onSubmitCommentReply={onSubmitCommentReply}
        showCommentReplyForm={showCommentReplyForm}
        commentReplyContent={commentReplyContent}
        setCommentReplyContent={setCommentReplyContent}
        submittingCommentReply={submittingCommentReply}
      />
    </div>
  );
};

// Quill modules configuration
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'align': [] }],
    ['link'],
    ['clean']
  ],
};

const CourseViewer = ({ userData }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [course, setCourse] = useState(null);
  const [currentComponentIndex, setCurrentComponentIndex] = useState(0);
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [error, setError] = useState('');
  const [referencedAssignments, setReferencedAssignments] = useState({});
  const [referencedContent, setReferencedContent] = useState({});
  const [validEnrollment, setValidEnrollment] = useState(null);
  const [enrollmentChecked, setEnrollmentChecked] = useState(false);
  const [componentSubmissions, setComponentSubmissions] = useState({});
  const [courseEnrollment, setCourseEnrollment] = useState(null);
  const [completionLoading, setCompletionLoading] = useState(false);
  const [completionMessage, setCompletionMessage] = useState(null);
  const [showResponseForm, setShowResponseForm] = useState(false);
  const [responseContent, setResponseContent] = useState('');
  const [submittingResponse, setSubmittingResponse] = useState(false);
  const [showCommentReplyForm, setShowCommentReplyForm] = useState(null);
  const [commentReplyContent, setCommentReplyContent] = useState('');
  const [submittingCommentReply, setSubmittingCommentReply] = useState(false);
  const [criticalCompletionError, setCriticalCompletionError] = useState(null);
  const [assignmentSubmissionByComponent, setAssignmentSubmissionByComponent] = useState({});
  const [fullAssignmentSubmissions, setFullAssignmentSubmissions] = useState({});
  const [assignmentSubmissionLoading, setAssignmentSubmissionLoading] = useState({});
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [activeResubmissions, setActiveResubmissions] = useState(new Set()); // Track which components have active resubmission forms

  // Helper function to load full assignment submission data
  const loadAssignmentSubmissionData = async (componentId, assignmentSubmissionId) => {
    if (!assignmentSubmissionId || fullAssignmentSubmissions[componentId]) {
      return; // Already loaded or no assignment submission ID
    }

    setAssignmentSubmissionLoading(prev => ({ ...prev, [componentId]: true }));
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/assignmentsubmissions/${assignmentSubmissionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const assignmentSubmissionData = response.data.submission;
      setFullAssignmentSubmissions(prev => ({
        ...prev,
        [componentId]: assignmentSubmissionData
      }));
      
      console.log('[CourseViewer] Loaded assignment submission data for component:', componentId, assignmentSubmissionData);
    } catch (error) {
      console.error('[CourseViewer] Error loading assignment submission data:', error);
    } finally {
      setAssignmentSubmissionLoading(prev => ({ ...prev, [componentId]: false }));
    }
  };

  // LMS assignment submit/resubmit emails: server sends from componentSubmissions →
  // sendLmsAssignmentSubmissionEmails (assignment creator + TOR w/ notification_preferences). No duplicate client mail.

  // Helper function to check Discussion Board completion requirements
  const checkDiscussionBoardCompletion = async (componentId, courseEnrollmentId, userId, isMainPostReply = false) => {
    try {
      const token = localStorage.getItem('token');
      
      // Get all comments for this discussion board
      const commentsResponse = await axios.get(`/api/comments?reference_id=${currentComponent.reference_id}&table=discussionboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const allComments = commentsResponse.data.comments;
      const userComments = allComments.filter(c => c.account === userId);
      
      console.log('[CourseViewer] All comments:', allComments);
      console.log('[CourseViewer] User comments:', userComments);
      console.log('[CourseViewer] Grade settings:', currentComponent.gradeSettings);
      console.log('[CourseViewer] Is main post reply:', isMainPostReply);
      
      // Get component grade settings
      const gradeSettings = currentComponent.gradeSettings;
      console.log('[CourseViewer] Raw gradeSettings:', gradeSettings);
      console.log('[CourseViewer] gradeSettings type:', typeof gradeSettings);
      
      if (!gradeSettings) {
        console.log('[CourseViewer] No grade settings found, defaulting to submitted');
        return { status: 'submitted', hoursEarned: null };
      }
      
      // Try different possible property names for criteria type
      const criteriaType = gradeSettings.criteria?.type || gradeSettings.criteriaType || gradeSettings.type || gradeSettings.criteria;
      console.log('[CourseViewer] criteriaType found:', criteriaType);
      
      let status = 'submitted';
      let hoursEarned = null;
      
      switch (criteriaType) {
        case 'respond_to_main':
          // Check if user has responded to main post (reference_id === parent means it's a response to main post)
          const hasMainResponse = userComments.some(c => c.reference_id === c.parent);
          console.log('[CourseViewer] Has main response (from existing comments):', hasMainResponse);
          
          if (isMainPostReply) {
            // If this is a main post reply, it should be completed (regardless of previous comments)
            status = 'completed';
            hoursEarned = currentComponent.hourawards ? 
              (typeof currentComponent.hourawards === 'string' ? JSON.parse(currentComponent.hourawards) : currentComponent.hourawards) : null;
            console.log('[CourseViewer] Main post reply - setting completed with hours:', hoursEarned);
          } else {
            // If this is a comment reply, check if user has main response
            if (hasMainResponse) {
              status = 'completed';
              hoursEarned = currentComponent.hourawards ? 
                (typeof currentComponent.hourawards === 'string' ? JSON.parse(currentComponent.hourawards) : currentComponent.hourawards) : null;
              console.log('[CourseViewer] Comment reply with existing main response - setting completed with hours:', hoursEarned);
            } else {
              status = 'submitted';
              hoursEarned = null;
              console.log('[CourseViewer] Comment reply without main response - setting submitted');
            }
          }
          break;
          
        case 'respond_to_main_and_students':
          // Determine counts from existing comments
          const required = gradeSettings.criteria?.min_responses || 1;
          const mainResponses = userComments.filter(c => c.reference_id === c.parent).length;
          // Count only responses to responses (replies to other students' comments)
          const responsesToResponses = userComments.filter(c => c.reference_id !== c.parent && c.parent !== c.reference_id).length;
          // Account for the current action that may not yet be persisted
          const effectiveMain = isMainPostReply ? Math.max(mainResponses, 1) : mainResponses;
          const effectiveResponsesToResponses = responsesToResponses; // do not add +1 to avoid skewing
          if (effectiveMain > 0 && effectiveResponsesToResponses >= required) {
            status = 'completed';
            hoursEarned = currentComponent.hourawards ?
              (typeof currentComponent.hourawards === 'string' ? JSON.parse(currentComponent.hourawards) : currentComponent.hourawards) : null;
            console.log('[CourseViewer] respond_to_main_and_students met. main:', effectiveMain, 'responses to responses:', effectiveResponsesToResponses, 'required:', required);
          } else {
            status = 'submitted';
            hoursEarned = null;
            console.log('[CourseViewer] respond_to_main_and_students not yet met. main:', effectiveMain, 'responses to responses:', effectiveResponsesToResponses, 'required:', required);
          }
          break;
          
        default:
          console.log('[CourseViewer] Unknown criteria type:', criteriaType);
          console.log('[CourseViewer] Available gradeSettings properties:', Object.keys(gradeSettings));
          // For other criteria types, keep as submitted
          status = 'submitted';
          hoursEarned = null;
      }
      
      console.log('[CourseViewer] Final completion check result:', { status, hoursEarned });
      return { status, hoursEarned };
    } catch (error) {
      console.error('[CourseViewer] Error checking discussion board completion:', error);
      return { status: 'submitted', hoursEarned: null };
    }
  };

  const command = searchParams.get('command');
  const courseId = searchParams.get('id');
  const componentIdFromUrl = searchParams.get('component_id');
  
  // Check if this is preview mode (instructor preview) or coursework mode (student access)
  const isPreviewMode = command === 'preview';
  const isCourseworkMode = command === 'coursework';
  
  // Show critical error if no valid command parameter
  const showCriticalError = !isPreviewMode && !isCourseworkMode;

  // Helper function to check if a component is a content component (not just a container)
  const isContentComponent = (component) => {
    return ['Content', 'Discussion Board', 'Assignment'].includes(component.type);
  };

  // Get all content components from the flat structure
  const getAllContentComponents = (components) => {
    return components.filter(component => isContentComponent(component));
  };

  // Check if component is completed (for coursework mode sequence restrictions)
  const isComponentCompleted = (componentId) => {
    if (!isCourseworkMode || !componentSubmissions[componentId]) return false;
    const submission = componentSubmissions[componentId];
    return submission.status === 'completed' || submission.graderesult === 'submitted' || submission.graderesult === 'completed';
  };

  // Special-case satisfaction: Assignments and Discussion Boards allow move-on when submitted
  const isComponentSatisfied = (componentId) => {
    if (!isCourseworkMode) return true;
    const comp = flatComponents.find(c => c.id === componentId);
    if (!comp) return false;
    const submission = componentSubmissions[componentId];
    if (comp.type === 'Assignment' || comp.type === 'Discussion Board') {
      return !!submission && (submission.status === 'submitted' || submission.status === 'completed');
    }
    return isComponentCompleted(componentId);
  };

  // Check if current component is completed/satisfied (for Next button restriction)
  const isCurrentComponentCompleted = () => {
    if (!isCourseworkMode || !currentComponent) return true; // Allow navigation in preview mode
    return isComponentSatisfied(currentComponent.id);
  };

  // Check if comments should be shown for current component
  const shouldShowComments = () => {
    if (!isCourseworkMode || !currentComponent) return false; // Only show in coursework mode
    return !!componentSubmissions[currentComponent.id]; // Show if there's a component submission
  };

  // Check if a component can be accessed (for sidebar navigation restrictions)
  const canAccessComponent = (component) => {
    if (!isCourseworkMode) return true; // Allow all navigation in preview mode
    
    // For coursework mode, check if this component is accessible
    const componentIndex = flatComponents.findIndex(c => c.id === component.id);
    if (componentIndex === -1) return false;
    
    // First component is always accessible
    if (componentIndex === 0) return true;
    
    // Check if all previous components are completed or satisfied
    for (let i = 0; i < componentIndex; i++) {
      const prevComponent = flatComponents[i];
      if (isContentComponent(prevComponent) && !isComponentSatisfied(prevComponent.id)) {
        return false; // Previous component not completed/satisfied
      }
    }
    
    return true; // All previous components completed/satisfied
  };

  // Set valid enrollment state (same pattern as FEeventRegistration.js)
  useEffect(() => {
    const enrollment = getValidEnrollment(userData);
    console.log('[CourseViewer] Valid enrollment:', enrollment);
    console.log('[CourseViewer] UserData:', userData);
    setValidEnrollment(enrollment);
    setEnrollmentChecked(true);
  }, [userData]);

  // Load assignment submission data when component submissions are loaded
  useEffect(() => {
    if (!componentSubmissions || Object.keys(componentSubmissions).length === 0) {
      return;
    }

    // Load assignment submission data for any component submissions that have assignmentsubmission
    Object.entries(componentSubmissions).forEach(([componentId, submission]) => {
      if (submission.assignmentsubmission) {
        loadAssignmentSubmissionData(componentId, submission.assignmentsubmission);
      }
    });
  }, [componentSubmissions]);

  // Load course data
  useEffect(() => {
    // Wait for enrollment check to complete for coursework mode
    if (isCourseworkMode && !enrollmentChecked) {
      return;
    }

    // Check authorization first - don't load course if not authorized
    if (!isPreviewMode && !isCourseworkMode) {
      setError('Unauthorized access - valid command required');
      setLoading(false);
      return;
    }

    if (!courseId) {
      setError('No course ID provided');
      setLoading(false);
      return;
    }

    const loadCourse = async () => {
      // Initialize submissions map (will be populated in coursework mode)
      let submissionsMap = {};
      
      // Check enrollment for coursework mode
      if (isCourseworkMode) {
        console.log('[CourseViewer] Coursework mode - checking enrollment');
        console.log('[CourseViewer] validEnrollment:', validEnrollment);
        if (!validEnrollment) {
          console.log('[CourseViewer] No valid enrollment found for coursework mode');
          setError('No valid enrollment found');
          setLoading(false);
          return;
        }
        console.log('[CourseViewer] Valid enrollment found:', validEnrollment);
        
        // Check if student is enrolled in this specific course
        try {
          console.log('[CourseViewer] Checking course enrollment for courseId:', courseId);
          console.log('[CourseViewer] Checking course enrollment for enrollment_id:', validEnrollment.enrollment_id);
          
          const token = localStorage.getItem('token');
          const response = await axios.get('/api/course-enrollments', {
            params: { 
              student: validEnrollment.enrollment_id,
              course: courseId,
              limit: 1
            },
            headers: { Authorization: `Bearer ${token}` }
          });
          
          console.log('[CourseViewer] Course enrollment API response:', response.data);
          
          const courseEnrollments = response.data.courseEnrollments || [];
          console.log('[CourseViewer] Course enrollments:', courseEnrollments);
          
          // Check each enrollment for proper matching
          courseEnrollments.forEach((enrollment, index) => {
            console.log(`[CourseViewer] Enrollment ${index}:`, {
              id: enrollment.id,
              course: enrollment.course,
              enrollment: enrollment.enrollment,
              status: enrollment.status,
              courseId_match: enrollment.course === courseId,
              enrollmentId_match: enrollment.enrollment === validEnrollment.enrollment_id
            });
          });
          
          // Check if student has an active enrollment in this course
          const matchingEnrollment = courseEnrollments.find(ce => 
            ce.course === courseId && 
            ce.enrollment === validEnrollment.enrollment_id
          );
          
          console.log('[CourseViewer] Matching enrollment:', matchingEnrollment);
          
          const isEnrolled = matchingEnrollment && matchingEnrollment.status === 'enrolled';
          console.log('[CourseViewer] Is enrolled:', isEnrolled);
          
          if (!isEnrolled) {
            console.log('[CourseViewer] Student not enrolled in course');
            setError('You are not enrolled in this course');
            setLoading(false);
            return;
          }
          
          console.log('[CourseViewer] Course enrollment verification successful');
          
          // Store the course enrollment for later use
          setCourseEnrollment(matchingEnrollment);
          
          // Check for existing component submissions to track progress
          try {
            console.log('[CourseViewer] Checking for existing component submissions');
            const submissionsResponse = await axios.get('/api/component-submissions', {
              params: { 
                course_enrollment: matchingEnrollment.id
              },
              headers: { Authorization: `Bearer ${token}` }
            });
            
            console.log('[CourseViewer] Component submissions response:', submissionsResponse.data);
            
            const submissions = submissionsResponse.data.componentSubmissions || [];
            console.log('[CourseViewer] Found component submissions:', submissions);
            
            // Create a map of component submissions by component ID
            submissions.forEach(submission => {
              submissionsMap[submission.component] = submission;
            });
            
            setComponentSubmissions(submissionsMap);
            console.log('[CourseViewer] Component submissions map:', submissionsMap);
            
          } catch (error) {
            console.error('[CourseViewer] Error fetching component submissions:', error);
            // Don't fail the course load for this - just log and continue
            setComponentSubmissions({});
          }
        } catch (error) {
          console.error('[CourseViewer] Error verifying course enrollment:', error);
          setError('Failed to verify course enrollment');
          setLoading(false);
          return;
        }
      }
      
      try {
        setLoading(true);
        setError('');
        
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Authentication token not found. Please log in again.');
        }

        // Fetch the course component and all of its children via viewer endpoint (no createdby restriction)
        const viewerResponse = await axios.get(`/api/components/viewer/${courseId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        const courseComponent = viewerResponse.data.component;
        
        if (!courseComponent || courseComponent.type !== 'Course') {
          throw new Error('Invalid course component');
        }

        if (!viewerResponse.data.components) {
          throw new Error('No components found for this course');
        }

        const allComponents = viewerResponse.data.components;
        
        // Sort components by sort_index to maintain proper sequence
        const sortedComponents = allComponents.sort((a, b) => {
          // First sort by sort_index
          if (a.sort_index !== b.sort_index) {
            return (a.sort_index || 0) - (b.sort_index || 0);
          }
          // If sort_index is the same, sort by createddate as fallback
          return new Date(a.createddate || 0) - new Date(b.createddate || 0);
        });
        
        // Create a course object with the components in the draft structure for compatibility
        const courseWithComponents = {
          ...courseComponent,
          draft: {
            components: sortedComponents
          }
        };

        setCourse(courseWithComponents);

        // Load referenced assignments if any
        await loadReferencedAssignments(sortedComponents);
        
        // Load referenced content if any
        await loadReferencedContent(sortedComponents);

        // Helper function to expand ancestors for a component
        const expandAncestorsForComponentId = (targetComponentId, components) => {
          const ancestors = new Set();
          let current = components.find(c => c.id === targetComponentId);
          const visited = new Set();
          while (current && current.parent && !visited.has(current.parent)) {
            ancestors.add(current.parent);
            visited.add(current.parent);
            current = components.find(c => c.id === current.parent);
          }
          return ancestors;
        };

        // Helper function to build flat components structure (same as in render)
        const buildFlatComponents = (components) => {
          const buildSectionsFromComponents = (components) => {
            const sections = [];
            const sectionComponents = components.filter(comp => comp.level === 'section');
            
            sectionComponents.forEach(sectionComp => {
              const section = {
                id: sectionComp.id,
                type: sectionComp.type,
                parent: sectionComp.parent,
                sort_index: sectionComp.sort_index,
                subsections: []
              };
              
              const subsectionComponents = components.filter(comp => 
                comp.level === 'subsection' && comp.parent === sectionComp.id
              );
              
              subsectionComponents.forEach(subsectionComp => {
                const subsection = {
                  id: subsectionComp.id,
                  type: subsectionComp.type,
                  parent: subsectionComp.parent,
                  sort_index: subsectionComp.sort_index,
                  nestedSections: []
                };
                
                const nestedComponents = components.filter(comp => 
                  comp.level === 'nested' && comp.parent === subsectionComp.id
                );
                
                nestedComponents.forEach(nestedComp => {
                  subsection.nestedSections.push({
                    id: nestedComp.id,
                    type: nestedComp.type,
                    parent: nestedComp.parent,
                    sort_index: nestedComp.sort_index
                  });
                });
                
                // Sort nested sections by sort_index
                subsection.nestedSections.sort((a, b) => (a.sort_index || 0) - (b.sort_index || 0));
                
                section.subsections.push(subsection);
              });
              
              // Sort subsections by sort_index
              section.subsections.sort((a, b) => (a.sort_index || 0) - (b.sort_index || 0));
              
              sections.push(section);
            });
            
            // Sort sections by sort_index
            sections.sort((a, b) => (a.sort_index || 0) - (b.sort_index || 0));
            
            return sections;
          };

          const sections = buildSectionsFromComponents(components);
          const flatComponents = [];
          sections.forEach(section => {
            flatComponents.push(section);
            section.subsections?.forEach(subsection => {
              flatComponents.push(subsection);
              subsection.nestedSections?.forEach(nestedSection => {
                flatComponents.push(nestedSection);
              });
            });
          });
          return flatComponents;
        };

        // Helper function to check if a component is satisfied (for access checking)
        const isComponentSatisfiedForAccess = (componentId, flatComponentsArray, submissions) => {
          if (!isCourseworkMode) return true;
          const comp = flatComponentsArray.find(c => c.id === componentId);
          if (!comp) return false;
          const submission = submissions[componentId];
          if (comp.type === 'Assignment' || comp.type === 'Discussion Board') {
            return !!submission && (submission.status === 'submitted' || submission.status === 'completed');
          }
          // For other types, check if completed
          return !!submission && (submission.status === 'completed' || submission.graderesult === 'submitted' || submission.graderesult === 'completed');
        };

        // Helper function to check if a component can be accessed (for coursework mode)
        const canAccessComponentById = (componentId, flatComponentsArray, submissions) => {
          if (!isCourseworkMode) return true; // Allow all navigation in preview mode
          
          const componentIndex = flatComponentsArray.findIndex(c => c.id === componentId);
          if (componentIndex === -1) return false;
          
          // First component is always accessible
          if (componentIndex === 0) return true;
          
          // Check if all previous content components are completed or satisfied
          for (let i = 0; i < componentIndex; i++) {
            const prevComponent = flatComponentsArray[i];
            if (isContentComponent(prevComponent) && !isComponentSatisfiedForAccess(prevComponent.id, flatComponentsArray, submissions)) {
              return false; // Previous component not completed/satisfied
            }
          }
          
          return true; // All previous components completed/satisfied
        };

        // Build flat components structure to find correct index
        const tempFlatComponents = buildFlatComponents(sortedComponents);

        // Auto-expand the first few sections for better UX
        const initialExpanded = new Set();
        sortedComponents.forEach((component, index) => {
            if (index < 3) { // Expand first 3 components
              initialExpanded.add(component.id);
            }
          });
        
        // Set initial component - check for component_id URL parameter first
        let targetComponentIndex = null;
        let targetComponentId = null;
        
        if (componentIdFromUrl) {
          // Try to find the component by component_id from URL in flatComponents
          const targetComponent = tempFlatComponents.find(c => c.id === componentIdFromUrl);
          if (targetComponent) {
            // Check access restrictions for coursework mode
            const hasAccess = canAccessComponentById(componentIdFromUrl, tempFlatComponents, submissionsMap);
            
            if (hasAccess) {
              targetComponentIndex = tempFlatComponents.findIndex(c => c.id === componentIdFromUrl);
              targetComponentId = componentIdFromUrl;
              console.log('[CourseViewer] Found component_id in URL, jumping to:', componentIdFromUrl, 'at index:', targetComponentIndex);
              
              // Expand ancestors so the component is visible in sidebar
              const ancestorIds = expandAncestorsForComponentId(targetComponentId, sortedComponents);
              ancestorIds.forEach(id => initialExpanded.add(id));
              // Also expand the target component itself if it's a container
              const targetComponentFromSorted = sortedComponents.find(c => c.id === componentIdFromUrl);
              if (targetComponentFromSorted && !isContentComponent(targetComponentFromSorted)) {
                initialExpanded.add(targetComponentId);
              }
            } else {
              console.warn('[CourseViewer] component_id from URL is not accessible (coursework restrictions):', componentIdFromUrl);
              // Will fall through to default to first accessible component
            }
          } else {
            console.warn('[CourseViewer] component_id from URL not found:', componentIdFromUrl);
          }
        }
        
        // If no component_id or not found/not accessible, find first accessible content component
        if (targetComponentIndex === null) {
          const initialContentComponents = getAllContentComponents(tempFlatComponents);
          
          // In coursework mode, find the first accessible component
          if (isCourseworkMode) {
            for (const contentComponent of initialContentComponents) {
              if (canAccessComponentById(contentComponent.id, tempFlatComponents, submissionsMap)) {
                targetComponentIndex = tempFlatComponents.findIndex(c => c.id === contentComponent.id);
                targetComponentId = contentComponent.id;
                console.log('[CourseViewer] Defaulting to first accessible component:', contentComponent.id);
                break;
              }
            }
          } else {
            // Preview mode - just use first content component
            if (initialContentComponents.length > 0) {
              const firstContentComponent = initialContentComponents[0];
              targetComponentIndex = tempFlatComponents.findIndex(c => c.id === firstContentComponent.id);
              targetComponentId = firstContentComponent.id;
            }
          }
        }
        
        // Make sure sidebar is not collapsed by default
        // Note: 'sidebar-collapsed' should NOT be in expandedSections for sidebar to be visible
        setExpandedSections(initialExpanded);
        
        // Set the current component index
        if (targetComponentIndex !== null && targetComponentIndex !== -1) {
          setCurrentComponentIndex(targetComponentIndex);
        }

      } catch (error) {
        console.error('Error loading course:', error);
        setError(error.response?.data?.error || error.message || 'Failed to load course');
      } finally {
        setLoading(false);
      }
    };

    loadCourse();
  }, [courseId, isPreviewMode, isCourseworkMode, userData, validEnrollment, enrollmentChecked]);

  // Load referenced assignments for Assignment type components
  const loadReferencedAssignments = async (components) => {
    try {
      const assignmentIds = components
        .filter(c => c.type === 'Assignment' && c.reference_table === 'assignments' && c.reference_id)
        .map(c => c.reference_id);

      if (assignmentIds.length === 0) return;

      const token = localStorage.getItem('token');
      const assignmentsResponse = await axios.get(`/api/assignments?ids=${assignmentIds.join(',')}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const assignmentsMap = {};
      assignmentsResponse.data.assignments.forEach(assignment => {
        assignmentsMap[assignment.id] = assignment;
      });

      setReferencedAssignments(assignmentsMap);
    } catch (error) {
      console.error('Error loading referenced assignments:', error);
    }
  };

  // Load referenced content for Content and Discussion Board type components (same as CourseBuilder)
  const loadReferencedContent = async (components) => {
    try {
      const contentComponents = components.filter(c => 
        (c.type === 'Content' || c.type === 'Discussion Board') && 
        c.reference_table === 'comments' && 
        c.reference_id
      );

      if (contentComponents.length === 0) return;

      const token = localStorage.getItem('token');
      const commentIds = contentComponents.map(c => c.reference_id).filter(id => id);

      if (commentIds.length === 0) return;

      // Use the same endpoint as CourseBuilder
      const commentsResponse = await axios.get(`/api/comments/ids?ids=${commentIds.join(',')}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const commentsMap = {};
      if (commentsResponse.data.comments) {
        commentsResponse.data.comments.forEach(comment => {
          commentsMap[comment.id] = comment;
        });
      }

      // Map comments to component IDs
      const contentMap = {};
      contentComponents.forEach(component => {
        if (commentsMap[component.reference_id]) {
          contentMap[component.id] = commentsMap[component.reference_id];
        }
      });

      setReferencedContent(contentMap);
    } catch (error) {
      console.error('Error loading referenced content:', error);
    }
  };

  // Toggle section expansion
  const toggleSection = (componentId) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(componentId)) {
        newSet.delete(componentId);
      } else {
        newSet.add(componentId);
      }
      return newSet;
    });
  };

  // Handler functions for Discussion Board response form
  const handleReplyClick = () => {
    setShowResponseForm(true);
    setResponseContent('');
    
    // Scroll to top of response container after a brief delay to ensure DOM update
    setTimeout(() => {
      const responseContainer = document.querySelector('.content-body');
      if (responseContainer) {
        responseContainer.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    }, 100);
  };

  const handleCancelReply = () => {
    setShowResponseForm(false);
    setResponseContent('');
  };

  // Handler to start resubmission (enable the form)
  const handleStartResubmission = (componentId) => {
    setActiveResubmissions(prev => new Set([...prev, componentId]));
  };

  // Handler to cancel resubmission (go back to view mode)
  const handleCancelResubmission = (componentId) => {
    setActiveResubmissions(prev => {
      const newSet = new Set(prev);
      newSet.delete(componentId);
      return newSet;
    });
  };

  const handleCommentReplyClick = (commentId) => {
    setShowCommentReplyForm(commentId);
    setCommentReplyContent('');
  };

  const handleCancelCommentReply = () => {
    setShowCommentReplyForm(null);
    setCommentReplyContent('');
  };

  const handleSubmitCommentReply = async (commentId) => {
    if (!commentReplyContent.trim()) return;
    
    setSubmittingCommentReply(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post('/api/comments', {
        content: commentReplyContent,
        type: 'Response',
        reference_id: currentComponent.reference_id, // Reference to the main post
        parent: commentId, // Parent is the comment being replied to
        table: 'discussionboard',
        relevantRole: 'Responder',
        account: userData?.account_id,
        // Set all settings to false for Response type comments
        settings: {
          allowMainResponse: false,
          allowCommentResponse: false,
          notifyMeOnResponses: false,
          allowAssigneesViewResponses: false
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('[CourseViewer] Comment reply submitted successfully');
      
      // If this is a Discussion Board component, also create/update a componentsubmissions item
      if (currentComponent.type === 'Discussion Board' && courseEnrollment) {
        // Check if we already have a component submission for this component
        const existingSubmission = componentSubmissions[currentComponent.id];
        
        if (!existingSubmission) {
          console.log('[CourseViewer] Creating componentsubmissions for Discussion Board comment reply');
          
          // Check completion requirements based on grade settings
          const completionCheck = await checkDiscussionBoardCompletion(
            currentComponent.id, 
            courseEnrollment.id, 
            userData.account_id,
            false // This is a comment reply, not a main post reply
          );
          
          // Enforce respond_to_main rule: replies to responses cannot complete
          const criteriaTypeForComment = currentComponent?.gradeSettings?.criteria?.type
            || currentComponent?.gradeSettings?.criteriaType
            || currentComponent?.gradeSettings?.type
            || currentComponent?.gradeSettings?.criteria;
          const enforcedStatus = (criteriaTypeForComment === 'respond_to_main') ? 'submitted' : completionCheck.status;
          const enforcedHours = (criteriaTypeForComment === 'respond_to_main') ? null : completionCheck.hoursEarned;
          
          const componentSubmissionPayload = {
            component: currentComponent.id,
            course_enrollment: courseEnrollment.id,
            enrollment: validEnrollment.enrollment_id,
            accesscard: validEnrollment.accesscard_id,
            account: userData.account_id,
            status: enforcedStatus,
            hours_earned: enforcedHours,
            completion_data: { completed: enforcedStatus === 'completed' }
          };
          
          const componentSubmissionResponse = await axios.post('/api/component-submissions', componentSubmissionPayload, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          console.log('[CourseViewer] Component submission created for comment reply:', componentSubmissionResponse.data);
          console.log('[CourseViewer] Current component:', currentComponent);
          
          // Update local state
          setComponentSubmissions(prev => ({
            ...prev,
            [currentComponent.id]: componentSubmissionResponse.data.componentSubmission
          }));
        } else if (existingSubmission.status === 'completed') {
          console.log('[CourseViewer] Component submission already completed, skipping update for comment reply');
        } else if (existingSubmission.status === 'submitted') {
          console.log('[CourseViewer] Updating existing componentsubmissions for Discussion Board comment reply');
          
          // Check completion requirements based on grade settings
          const completionCheck = await checkDiscussionBoardCompletion(
            currentComponent.id, 
            courseEnrollment.id, 
            userData.account_id,
            false // This is a comment reply, not a main post reply
          );
          
          // Enforce respond_to_main rule: replies to responses cannot complete
          const criteriaTypeForComment = currentComponent?.gradeSettings?.criteria?.type
            || currentComponent?.gradeSettings?.criteriaType
            || currentComponent?.gradeSettings?.type
            || currentComponent?.gradeSettings?.criteria;
          const enforcedStatus = (criteriaTypeForComment === 'respond_to_main') ? 'submitted' : completionCheck.status;
          const enforcedHours = (criteriaTypeForComment === 'respond_to_main') ? null : completionCheck.hoursEarned;
          
          // Only update if status has changed or hours have changed
          if (enforcedStatus !== existingSubmission.status || 
              JSON.stringify(enforcedHours) !== JSON.stringify(existingSubmission.hours_earned)) {
            
            const updatePayload = {
              status: enforcedStatus,
              hours_earned: enforcedHours,
              completion_data: { completed: enforcedStatus === 'completed' }
            };
            
            const componentSubmissionResponse = await axios.put(`/api/component-submissions/${existingSubmission.id}`, updatePayload, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            console.log('[CourseViewer] Component submission updated for comment reply:', componentSubmissionResponse.data);
            
            // Update local state
            setComponentSubmissions(prev => ({
              ...prev,
              [currentComponent.id]: componentSubmissionResponse.data.componentSubmission
            }));
          } else {
            console.log('[CourseViewer] Component submission status unchanged, no update needed for comment reply');
          }
        }
      }
      
      setCommentReplyContent('');
      setShowCommentReplyForm(null);
      
      // Trigger a refresh of the discussion responses
      window.dispatchEvent(new CustomEvent('refreshDiscussionResponses'));
    } catch (error) {
      console.error('[CourseViewer] Error submitting comment reply:', error);
      
      // Check if it's a duplicate key constraint error
      const errorMessage = error.response?.data?.error || error.message || '';
      const isDuplicateKeyError = errorMessage.toLowerCase().includes('duplicate key value violates unique constraint') ||
                                errorMessage.toLowerCase().includes('duplicate key') ||
                                errorMessage.toLowerCase().includes('unique constraint');
      
      if (isDuplicateKeyError) {
        // Show critical error popup that forces reload
        setCriticalCompletionError({
          title: "Duplicate Submission Detected",
          message: "This component has already been completed. The page needs to be reloaded to sync your progress.",
          details: "This usually happens when you've already completed this component in another tab or session. Please reload the page to continue.",
          showReloadButton: true,
          reloadButtonText: "Reload Page",
          onReloadClick: () => window.location.reload()
        });
      } else {
        // Show critical error popup for all other failures
        setCriticalCompletionError({
          title: "Comment Reply Failed",
          message: "Failed to submit comment reply. The page needs to be reloaded to try again.",
          details: "Please reload the page and attempt to submit your reply again. If the problem persists, contact your instructor.",
          showReloadButton: true,
          reloadButtonText: "Reload Page",
          onReloadClick: () => window.location.reload()
        });
      }
    } finally {
      setSubmittingCommentReply(false);
    }
  };

  const handleSubmitReply = async () => {
    if (!responseContent.trim()) return;
    
    setSubmittingResponse(true);
    try {
      const token = localStorage.getItem('token');
      
      // Submit the discussion response
      const response = await axios.post('/api/comments', {
        content: responseContent,
        type: 'Response',
        reference_id: currentComponent.reference_id,
        parent: currentComponent.reference_id,
        table: 'discussionboard', 
        relevantRole: 'Responder',
        account: userData?.account_id,
        // Set all settings to false for Response type comments
        settings: {
          allowMainResponse: false,
          allowCommentResponse: false,
          notifyMeOnResponses: false,
          allowAssigneesViewResponses: false
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('[CourseViewer] Response submitted successfully:', response.data);
      
      // If this is a Discussion Board component, also create/update a componentsubmissions item
      if (currentComponent.type === 'Discussion Board' && courseEnrollment) {
        // Check if we already have a component submission for this component
        const existingSubmission = componentSubmissions[currentComponent.id];
        
        if (!existingSubmission) {
          console.log('[CourseViewer] Creating componentsubmissions for Discussion Board completion');
          
          // Check completion requirements based on grade settings
          const completionCheck = await checkDiscussionBoardCompletion(
            currentComponent.id, 
            courseEnrollment.id, 
            userData.account_id,
            true // This is a main post reply
          );
          
          const componentSubmissionPayload = {
            component: currentComponent.id,
            course_enrollment: courseEnrollment.id,
            enrollment: validEnrollment.enrollment_id,
            accesscard: validEnrollment.accesscard_id,
            account: userData.account_id,
            status: completionCheck.status,
            hours_earned: completionCheck.hoursEarned,
            completion_data: { completed: completionCheck.status === 'completed' }
          };
          
          const componentSubmissionResponse = await axios.post('/api/component-submissions', componentSubmissionPayload, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          console.log('[CourseViewer] Component submission created:', componentSubmissionResponse.data);
          
          // Update local state
          setComponentSubmissions(prev => ({
            ...prev,
            [currentComponent.id]: componentSubmissionResponse.data.componentSubmission
          }));
        } else if (existingSubmission.status === 'completed') {
          console.log('[CourseViewer] Component submission already completed, skipping update');
        } else if (existingSubmission.status === 'submitted') {
          console.log('[CourseViewer] Updating existing componentsubmissions for Discussion Board main reply');
          
          // Check completion requirements based on grade settings
          const completionCheck = await checkDiscussionBoardCompletion(
            currentComponent.id, 
            courseEnrollment.id, 
            userData.account_id,
            true // This is a main post reply
          );
          
          // Only update if status has changed or hours have changed
          if (completionCheck.status !== existingSubmission.status || 
              JSON.stringify(completionCheck.hoursEarned) !== JSON.stringify(existingSubmission.hours_earned)) {
            
            const updatePayload = {
              status: completionCheck.status,
              hours_earned: completionCheck.hoursEarned,
              completion_data: { completed: completionCheck.status === 'completed' }
            };
            
            const componentSubmissionResponse = await axios.put(`/api/component-submissions/${existingSubmission.id}`, updatePayload, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            console.log('[CourseViewer] Component submission updated for main reply:', componentSubmissionResponse.data);
            
            // Update local state
            setComponentSubmissions(prev => ({
              ...prev,
              [currentComponent.id]: componentSubmissionResponse.data.componentSubmission
            }));
          } else {
            console.log('[CourseViewer] Component submission status unchanged, no update needed for main reply');
          }
        }
      }
      
      setResponseContent('');
      setShowResponseForm(false);
      console.log('[CourseViewer] Discussion Board response and component submission completed');
      
      // Trigger a refresh of the discussion responses
      window.dispatchEvent(new CustomEvent('refreshDiscussionResponses'));
    } catch (error) {
      console.error('[CourseViewer] Error submitting response:', error);
      
      // Check if it's a duplicate key constraint error
      const errorMessage = error.response?.data?.error || error.message || '';
      const isDuplicateKeyError = errorMessage.toLowerCase().includes('duplicate key value violates unique constraint') ||
                                errorMessage.toLowerCase().includes('duplicate key') ||
                                errorMessage.toLowerCase().includes('unique constraint');
      
      if (isDuplicateKeyError) {
        // Show critical error popup that forces reload
        setCriticalCompletionError({
          title: "Duplicate Submission Detected",
          message: "This component has already been completed. The page needs to be reloaded to sync your progress.",
          details: "This usually happens when you've already completed this component in another tab or session. Please reload the page to continue.",
          showReloadButton: true,
          reloadButtonText: "Reload Page",
          onReloadClick: () => window.location.reload()
        });
      } else {
        // Show critical error popup for all other failures
        setCriticalCompletionError({
          title: "Discussion Response Failed",
          message: "Failed to submit discussion response. The page needs to be reloaded to try again.",
          details: "Please reload the page and attempt to submit your response again. If the problem persists, contact your instructor.",
          showReloadButton: true,
          reloadButtonText: "Reload Page",
          onReloadClick: () => window.location.reload()
        });
      }
    } finally {
      setSubmittingResponse(false);
    }
  };

  // Expand ancestor containers in the sidebar for a given component
  const expandAncestorsForComponent = (componentId) => {
    if (!components || components.length === 0 || !componentId) return;
    const ancestors = [];
    let current = components.find(c => c.id === componentId);
    const visited = new Set();
    while (current && current.parent && !visited.has(current.parent)) {
      ancestors.push(current.parent);
      visited.add(current.parent);
      current = components.find(c => c.id === current.parent);
    }
    if (ancestors.length > 0) {
      setExpandedSections(prev => new Set([...prev, ...ancestors]));
    }
  };

  // Get the next content component index
  const getNextContentIndex = (currentIndex) => {
    if (!contentComponents || contentComponents.length === 0) return currentIndex;
    
    const currentContentIndex = contentComponents.findIndex(c => c.id === currentComponent?.id);
    
    if (currentContentIndex === -1 || currentContentIndex === contentComponents.length - 1) {
      return currentIndex; // No next content component found
    }
    
    // Find the index of the next content component in the flat components array
    const nextComponent = contentComponents[currentContentIndex + 1];
    return flatComponents.findIndex(c => c.id === nextComponent.id);
  };

  // Get the previous content component index
  const getPreviousContentIndex = (currentIndex) => {
    if (!contentComponents || contentComponents.length === 0) return currentIndex;
    
    const currentContentIndex = contentComponents.findIndex(c => c.id === currentComponent?.id);
    
    if (currentContentIndex <= 0) {
      return currentIndex; // No previous content component found
    }
    
    // Find the index of the previous content component in the flat components array
    const prevComponent = contentComponents[currentContentIndex - 1];
    return flatComponents.findIndex(c => c.id === prevComponent.id);
  };

  // Navigate to next content component
  const goToNext = () => {
    if (!flatComponents || flatComponents.length === 0) return;
    
    const nextIndex = getNextContentIndex(currentComponentIndex);
    if (nextIndex !== currentComponentIndex) {
      setCurrentComponentIndex(nextIndex);
      
      // Auto-expand the section if it's collapsed
      const nextComponent = flatComponents[nextIndex];
      if (nextComponent && !expandedSections.has(nextComponent.id)) {
        setExpandedSections(prev => new Set([...prev, nextComponent.id]));
      }
      // Ensure all parent containers of the next component are expanded
      expandAncestorsForComponent(nextComponent?.id);
    }
  };

  // Navigate to previous content component
  const goToPrevious = () => {
    const prevIndex = getPreviousContentIndex(currentComponentIndex);
    if (prevIndex !== currentComponentIndex) {
      setCurrentComponentIndex(prevIndex);
      
      // Auto-expand the section if it's collapsed
      const prevComponent = flatComponents[prevIndex];
      if (prevComponent && !expandedSections.has(prevComponent.id)) {
        setExpandedSections(prev => new Set([...prev, prevComponent.id]));
      }
      // Ensure all parent containers of the previous component are expanded
      expandAncestorsForComponent(prevComponent?.id);
    }
  };

  // Navigate to specific component (only allow navigation to content components)
  const goToComponent = (index) => {
    const component = flatComponents[index];
    
    // If it's a container component (Module/Lesson), toggle its children visibility
    if (component && !isContentComponent(component)) {
      toggleSection(component.id);
      return;
    }
    
    // If it's a content component, navigate to it
    setCurrentComponentIndex(index);
    
    // Auto-expand the section if it's collapsed
    if (component && !expandedSections.has(component.id)) {
      setExpandedSections(prev => new Set([...prev, component.id]));
    }
    // Ensure all parent containers of the selected component are expanded
    expandAncestorsForComponent(component?.id);
  };

  // Handle container component click (expand/collapse)
  const handleContainerClick = (componentId) => {
    toggleSection(componentId);
  };

  // Helper function to render image from tag (copied from ProjectsDirectory.js)
  const renderImageFromTag = (imgTag) => {
    const match = imgTag.match(/src=\"([^\"]+)\".*width=\"(\d+)\".*height=\"(\d+)\"/);
    if (match) {
      const src = match[1];
      const width = match[2];
      const height = match[3];
      
      // Check if the image source is a Canva.com link
      if (src.includes('canva.com')) {
        // Extract the Canva design ID from the URL
        const canvaMatch = src.match(/canva\.com\/design\/([^\/]+)/);
        if (canvaMatch) {
          const designId = canvaMatch[1];
          return (
            <embed
              src={`https://www.canva.com/design/${designId}/view?embed`}
              width={width}
              height={height}
              style={{ border: '1px solid #ccc' }}
              title="Canva Design"
            />
          );
        }
      }
      
      // Default img tag for non-Canva images
      return <img src={src} width={width} height={height} alt="Image" style={{ border: '1px solid #ccc', maxWidth: '100%', height: 'auto' }} />;
    }
    return null;
  };

  // Helper function to render video from URL (copied from ProjectsDirectory.js)
  const renderVideoFromUrl = (url) => {
    if (!url) return null;
    
    const w = 800;
    const h = 450;

    // Vimeo embed
    if (/vimeo\.com/.test(url)) {
      const id = url.split('/').pop();
      return (
        <iframe
          src={`https://player.vimeo.com/video/${id}`}
          width={w}
          height={h}
          frameBorder="0"
          allow="autoplay; fullscreen"
          allowFullScreen
          title="Vimeo Video"
        />
      );
    }

    // YouTube embed
    if (/youtube\.com|youtu\.be/.test(url)) {
      let id = url;
      if (url.includes('youtu.be/')) id = url.split('youtu.be/')[1];
      else if (url.includes('v=')) id = url.split('v=')[1].split('&')[0];
      return (
        <iframe
          width={w}
          height={h}
          src={`https://www.youtube.com/embed/${id}`}
          frameBorder="0"
          allow="autoplay; encrypted-media"
          allowFullScreen
          title="YouTube Video"
        />
      );
    }

    // Fallback to HTML5 <video> tag
    return (
      <video
        src={url}
        width={w}
        height={h}
        controls
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        style={{ backgroundColor: "#000" }}
      />
    );
  };

  // Get component display title (for content-header - matches sidebar outline)
  const getComponentTitle = (component) => {
    return component.title || `Untitled ${component.type}`;
  };

  // Render component content based on type
  const renderComponentContent = (component) => {
    switch (component.type) {
      case 'Module':
        return (
          <div className="module-content">
            <h3>{getComponentTitle(component)}</h3>
            {component.post_title && (
              <p className="module-description">{component.post_title}</p>
            )}
          </div>
        );
        
      case 'Lesson':
        return (
          <div className="lesson-content">
            <h4>{getComponentTitle(component)}</h4>
            {component.post_title && (
              <p className="lesson-description">{component.post_title}</p>
            )}
          </div>
        );
        
             case 'Content':
         const contentData = referencedContent[component.id];
         let parsedContent = [];
         if (contentData && contentData.content) {
           try {
             parsedContent = typeof contentData.content === 'string' 
               ? JSON.parse(contentData.content) 
               : contentData.content;
           } catch (error) {
             console.error('Error parsing content:', error);
             parsedContent = [];
           }
         }
         
         // Check if there's no component submission for this component (for debugging)
         if (isCourseworkMode && !componentSubmissions[component.id]) {
           console.log('[CourseViewer] No component submission found for component:', component);
         }
         
         // Check if this Content component needs a completion button
         const needsCompletionButton = () => {
           if (!isCourseworkMode || component.type !== 'Content') return false;
           
           const gradeSettings = component.gradeSettings;
           if (!gradeSettings) return false;
           
           let parsedGradeSettings;
           try {
             parsedGradeSettings = typeof gradeSettings === 'string' 
               ? JSON.parse(gradeSettings) 
               : gradeSettings;
           } catch (e) {
             return false;
           }
           
           return parsedGradeSettings?.type === 'completion' && parsedGradeSettings?.criteria === 'click_complete';
         };

         // Handle component completion
         const handleComponentCompletion = async () => {
           if (!isCourseworkMode || !courseEnrollment) {
             console.log('[CourseViewer] Not in coursework mode or no course enrollment');
             return;
           }

           setCompletionLoading(true);
           setCompletionMessage(null);

           try {
             console.log('[CourseViewer] Completing component:', component.id);
             
             const token = localStorage.getItem('token');
             
             // Parse hours earned from component.hourawards
             let hoursEarned = null;
             if (component.hourawards) {
               try {
                 const hourawards = typeof component.hourawards === 'string' 
                   ? JSON.parse(component.hourawards) 
                   : component.hourawards;
                 hoursEarned = hourawards;
               } catch (e) {
                 console.error('[CourseViewer] Error parsing hourawards:', e);
               }
             }
             
             const payload = {
               component: component.id,
               course_enrollment: courseEnrollment.id,
               enrollment: validEnrollment.enrollment_id,
               accesscard: validEnrollment.accesscard_id,
               account: userData.account_id, // Use userData.account_id instead
               status: 'completed',
               hours_earned: hoursEarned, // Pass as object, not stringified
               completion_data: { completed: true } // Pass as object, not stringified
             };

             const response = await axios.post('/api/component-submissions', payload, {
               headers: { Authorization: `Bearer ${token}` }
             });

             console.log('[CourseViewer] Component completion response:', response.data);

             // Update local state
             setComponentSubmissions(prev => ({
               ...prev,
               [component.id]: response.data.componentSubmission
             }));

             // Show success message
             setCompletionMessage({
               type: 'success',
               text: 'Component completed successfully!'
             });

             // Auto-hide success message after 1.5 seconds
             setTimeout(() => {
               setCompletionMessage(null);
             }, 1500);

           } catch (error) {
             console.error('[CourseViewer] Error completing component:', error);
             
             // Check if it's a duplicate key constraint error
             const errorMessage = error.response?.data?.error || error.message || '';
             const isDuplicateKeyError = errorMessage.toLowerCase().includes('duplicate key value violates unique constraint') ||
                                       errorMessage.toLowerCase().includes('duplicate key') ||
                                       errorMessage.toLowerCase().includes('unique constraint');
             
             if (isDuplicateKeyError) {
               // Show critical error popup that forces reload
               setCriticalCompletionError({
                 title: "Duplicate Submission Detected",
                 message: "This component has already been completed. The page needs to be reloaded to sync your progress.",
                 details: "This usually happens when you've already completed this component in another tab or session. Please reload the page to continue.",
                 showReloadButton: true,
                 reloadButtonText: "Reload Page",
                 onReloadClick: () => window.location.reload()
               });
             } else {
               // Show critical error popup for all other completion failures
               setCriticalCompletionError({
                 title: "Component Completion Failed",
                 message: "Failed to complete component. The page needs to be reloaded to try again.",
                 details: "Please reload the page and attempt to complete the component again. If the problem persists, contact your instructor.",
                 showReloadButton: true,
                 reloadButtonText: "Reload Page",
                 onReloadClick: () => window.location.reload()
               });
             }
           } finally {
             setCompletionLoading(false);
           }
         };

         const isCompleted = isComponentCompleted(component.id);
         const showCompletionButton = needsCompletionButton();

         return (
           <div className="content-item">
             <h5>{contentData ? contentData.title : component.title || 'Untitled'}</h5>
             <div className="content-preview">
               {parsedContent && Array.isArray(parsedContent) && parsedContent.length > 0 ? (
                 parsedContent.map((item, itemIndex) => (
                   <div key={item._id || itemIndex} style={{ marginBottom: itemIndex < parsedContent.length - 1 ? 16 : 0 }}>
                     {item.element === 'richText' && <div dangerouslySetInnerHTML={{ __html: item.value }} />}
                     {item.element === 'image' && renderImageFromTag(item.value)}
                     {item.element === 'video' && renderVideoFromUrl(item.value)}
                     {item.element === 'embed' && <div dangerouslySetInnerHTML={{ __html: item.value }} />}
                   </div>
                 ))
               ) : (
                 <div style={{ color: '#666', fontStyle: 'italic' }}>No content available</div>
               )}
             </div>
             
             {/* Completion Button - Only show if not completed */}
             {showCompletionButton && !isCompleted && (
               <div style={{ marginTop: '2rem', textAlign: 'center' }}>
                 <button
                   onClick={handleComponentCompletion}
                   disabled={completionLoading}
                   style={{
                     background: completionLoading 
                       ? 'linear-gradient(135deg, #6c757d 0%, #5a6268 100%)'
                       : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                     color: 'white',
                     borderRadius: '12px',
                     minWidth: '120px',
                     height: '40px',
                     display: 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     fontSize: '16px',
                     fontWeight: '700',
                     boxShadow: completionLoading 
                       ? '0 3px 8px rgba(108, 117, 125, 0.4)'
                       : '0 3px 8px rgba(25, 118, 210, 0.4)',
                     border: '2px solid white',
                     cursor: completionLoading ? 'not-allowed' : 'pointer',
                     transition: 'all 0.3s ease',
                     animation: completionLoading ? 'none' : 'pulseBlue 2s infinite',
                     margin: '0 auto',
                     opacity: completionLoading ? 0.7 : 1
                   }}
                   onMouseOver={(e) => {
                     if (!completionLoading) {
                       e.target.style.transform = 'scale(1.05)';
                       e.target.style.boxShadow = '0 5px 12px rgba(25, 118, 210, 0.6)';
                     }
                   }}
                   onMouseOut={(e) => {
                     if (!completionLoading) {
                       e.target.style.transform = 'scale(1)';
                       e.target.style.boxShadow = '0 3px 8px rgba(25, 118, 210, 0.4)';
                     }
                   }}
                 >
                   {completionLoading ? 'Completing...' : 'Complete'}
                 </button>
                 <div style={{
                   fontSize: '12px',
                   color: '#666',
                   fontStyle: 'italic',
                   marginTop: '8px'
                 }}>
                   Click this button if you are ready to move on
                 </div>
                 
                 {/* Completion Message */}
                 {completionMessage && (
                   <div style={{
                     marginTop: '1rem',
                     padding: '12px 16px',
                     borderRadius: '8px',
                     fontSize: '14px',
                     fontWeight: '600',
                     textAlign: 'center',
                     backgroundColor: completionMessage.type === 'success' ? '#d4edda' : '#f8d7da',
                     color: completionMessage.type === 'success' ? '#155724' : '#721c24',
                     border: `1px solid ${completionMessage.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
                   }}>
                     {completionMessage.text}
                   </div>
                 )}
               </div>
             )}
           </div>
         );
        
             case 'Discussion Board':
         const discussionData = referencedContent[component.id];
         
         // Console log Discussion Board settings
         if (discussionData) {
           console.log('[CourseViewer] Discussion Board settings:', {
             allowmainresponse: discussionData.allowmainresponse,
             allowcommentresponse: discussionData.allowcommentresponse,
             notifymeonresponses: discussionData.notifymeonresponses,
             allowassigneesviewresponses: discussionData.allowassigneesviewresponses,
             hideresponsesuntilresponded: discussionData.hideresponsesuntilresponded
           });
         }
         
         let parsedDiscussionContent = [];
         if (discussionData && discussionData.content) {
           try {
             parsedDiscussionContent = typeof discussionData.content === 'string' 
               ? JSON.parse(discussionData.content) 
               : discussionData.content;
           } catch (error) {
             console.error('Error parsing discussion content:', error);
             parsedDiscussionContent = [];
           }
         }
         
         return (
           <div className="discussion-item">
             <h5>{discussionData ? discussionData.title : component.title || 'Untitled'}</h5>
             <div className="content-preview">
               {parsedDiscussionContent && Array.isArray(parsedDiscussionContent) && parsedDiscussionContent.length > 0 ? (
                 parsedDiscussionContent.map((item, itemIndex) => (
                   <div key={item._id || itemIndex} style={{ marginBottom: itemIndex < parsedDiscussionContent.length - 1 ? 16 : 0 }}>
                     {item.element === 'richText' && <div dangerouslySetInnerHTML={{ __html: item.value }} />}
                     {item.element === 'image' && renderImageFromTag(item.value)}
                     {item.element === 'video' && renderVideoFromUrl(item.value)}
                     {item.element === 'embed' && <div dangerouslySetInnerHTML={{ __html: item.value }} />}
                   </div>
                 ))
               ) : (
                 <div style={{ color: '#666', fontStyle: 'italic' }}>No discussion content available</div>
               )}
             </div>
             
             {/* Response Permissions Info */}
             {isCourseworkMode && discussionData && (
               <ResponsePermissionsInfo 
                 discussionData={discussionData}
                 currentComponent={currentComponent}
                 userData={userData}
               />
             )}
             
             {/* Comment Statistics */}
             {isCourseworkMode && discussionData && discussionData.allowmainresponse === true && currentComponent && currentComponent.reference_id && userData?.account_id && (
               <CommentStatistics 
                 referenceId={currentComponent.reference_id}
                 userId={userData.account_id}
                 showAllComments={true}
               />
             )}
             
             {/* Reply Button - Only show if allowmainresponse is true and form is not open */}
             {isCourseworkMode && discussionData && discussionData.allowmainresponse === true && !showResponseForm && (
               <div style={{ marginTop: '1rem' }}>
                 <button
                   onClick={handleReplyClick}
                   style={{
                     backgroundColor: '#1976d2',
                     color: 'white',
                     border: 'none',
                     padding: '8px 16px',
                     borderRadius: '6px',
                     fontSize: '14px',
                     cursor: 'pointer',
                     transition: 'background-color 0.2s'
                   }}
                   onMouseOver={(e) => e.target.style.backgroundColor = '#1565c0'}
                   onMouseOut={(e) => e.target.style.backgroundColor = '#1976d2'}
                 >
                   Respond to Main Post
                 </button>
                </div>
              )}

              {/* Discussion Responses */}
              {isCourseworkMode && discussionData && discussionData.allowmainresponse === true && currentComponent && currentComponent.reference_id && userData?.account_id && (
                <DiscussionResponses 
                  discussionData={discussionData}
                  currentComponent={currentComponent}
                  userData={userData}
                  onCommentReplyClick={handleCommentReplyClick}
                  onCancelCommentReply={handleCancelCommentReply}
                  onSubmitCommentReply={handleSubmitCommentReply}
                  showCommentReplyForm={showCommentReplyForm}
                  commentReplyContent={commentReplyContent}
                  setCommentReplyContent={setCommentReplyContent}
                  submittingCommentReply={submittingCommentReply}
                />
              )}
              
              {/* Response Form */}
             {showResponseForm && isCourseworkMode && discussionData && discussionData.allowmainresponse === true && (
               <div style={{ 
                 marginTop: '1rem', 
                 padding: '1rem', 
                 backgroundColor: '#f9fafb', 
                 borderRadius: '8px', 
                 border: '1px solid #e5e7eb' 
               }}>
                 <div style={{ marginBottom: '1rem' }}>
                   <ReactQuill
                     value={responseContent}
                     onChange={setResponseContent}
                     modules={quillModules}
                     placeholder="Write your response to the main post..."
                     style={{
                       backgroundColor: 'white',
                       borderRadius: '6px'
                     }}
                   />
                 </div>
                 <div style={{ display: 'flex', gap: '8px' }}>
                   <button
                     onClick={handleSubmitReply}
                     disabled={submittingResponse || !responseContent.trim()}
                     style={{
                       backgroundColor: submittingResponse ? '#6c757d' : '#1976d2',
                       color: 'white',
                       border: 'none',
                       padding: '8px 16px',
                       borderRadius: '6px',
                       fontSize: '14px',
                       cursor: submittingResponse ? 'not-allowed' : 'pointer',
                       transition: 'background-color 0.2s'
                     }}
                   >
                     {submittingResponse ? 'Submitting...' : 'Submit Response'}
                   </button>
                   <button
                     onClick={handleCancelReply}
                     style={{
                       backgroundColor: '#6c757d',
                       color: 'white',
                       border: 'none',
                       padding: '8px 16px',
                       borderRadius: '6px',
                       fontSize: '14px',
                       cursor: 'pointer',
                       transition: 'background-color 0.2s'
                     }}
                   >
                     Cancel
                   </button>
                 </div>
               </div>
             )}
           </div>
         );
        
      case 'Assignment':
        const assignment = referencedAssignments[component.reference_id];
        // Determine interactivity based on coursework mode and existing component submission
        const compSubForAssignment = componentSubmissions[component.id];
        const canSubmitAssignment = isCourseworkMode && !compSubForAssignment;
        const isSubmittedOrCompleted = !!compSubForAssignment && (compSubForAssignment.status === 'submitted' || compSubForAssignment.status === 'completed');
        
        // Check if assignment is Points type (for resubmission)
        // Allow resubmission if:
        // 1. Instructor has explicitly allowed it (allow_resubmission === true) - ignore attempts, OR
        // 2. Status is 'submitted' (not yet graded) - ignore attempts, allow until instructor grades
        const isPointsAssignment = assignment && assignment.gradingresulttype === 'Points';
        const isSubmitted = compSubForAssignment?.status === 'submitted';
        const allowResubmission = compSubForAssignment?.allow_resubmission === true;
        const canResubmit = isCourseworkMode && isPointsAssignment && compSubForAssignment?.assignmentsubmission && 
          (allowResubmission || isSubmitted);
        
        const cachedSubmission = assignmentSubmissionByComponent[component.id];
        const fullAssignmentSubmission = fullAssignmentSubmissions[component.id];
        const isLoadingAssignmentSubmission = assignmentSubmissionLoading[component.id];
        
        // Check if resubmission form is active for this component
        const isResubmissionActive = activeResubmissions.has(component.id);
        
        return (
          <div className="assignment-item">
            {assignment ? (
              isSubmittedOrCompleted && !canResubmit ? (
                <div>
                  {isLoadingAssignmentSubmission ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                      <LoadingSpinner />
                      <div style={{ marginTop: '1rem', color: '#666' }}>Loading assignment submission...</div>
                    </div>
                  ) : (
                    <AssignmentViewer 
                      assignment={assignment}
                      mode={'view'}
                      interactive={false}
                      userData={userData}
                      enrollmentId={validEnrollment ? validEnrollment.enrollment_id : undefined}
                      submission={fullAssignmentSubmission || (cachedSubmission ? { submissiondata: cachedSubmission.submissiondata, graderesult: cachedSubmission.graderesult } : undefined)}
                    />
                  )}
                </div>
              ) : canResubmit && !isResubmissionActive ? (
                // Show previous submission with "Start Resubmission" button
                <div>
                  <div style={{
                    padding: '1rem',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffeeba',
                    borderRadius: '8px',
                    color: '#856404',
                    fontWeight: '600',
                    marginBottom: '1rem',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <span>Your previous submission is shown below. Click "Start Resubmission" to create a new submission.</span>
                    <button
                      onClick={() => handleStartResubmission(component.id)}
                      style={{
                        padding: '8px 16px',
                        backgroundColor: '#1976d2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'background-color 0.2s'
                      }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#1565c0'}
                      onMouseOut={(e) => e.target.style.backgroundColor = '#1976d2'}
                    >
                      Start Resubmission
                    </button>
                  </div>
                  <AssignmentViewer 
                    assignment={assignment}
                    mode={'view'}
                    interactive={false}
                    userData={userData}
                    enrollmentId={validEnrollment ? validEnrollment.enrollment_id : undefined}
                    submission={fullAssignmentSubmission || (cachedSubmission ? { submissiondata: cachedSubmission.submissiondata, graderesult: cachedSubmission.graderesult } : undefined)}
                  />
                </div>
              ) : (
                // Show editable form (new submission or active resubmission)
                <div>
                  {isResubmissionActive && (
                    <div style={{
                      padding: '1rem',
                      backgroundColor: '#e3f2fd',
                      border: '1px solid #90caf9',
                      borderRadius: '8px',
                      color: '#1565c0',
                      fontWeight: '600',
                      marginBottom: '1rem',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span>Creating a new submission. Your previous submission will be overwritten.</span>
                      <button
                        onClick={() => handleCancelResubmission(component.id)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '13px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#5a6268'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#6c757d'}
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                  <AssignmentViewer 
                    assignment={assignment}
                    mode={canSubmitAssignment || isResubmissionActive ? 'submit' : 'view'}
                    interactive={!!(canSubmitAssignment || isResubmissionActive)}
                    userData={userData}
                    enrollmentId={validEnrollment ? validEnrollment.enrollment_id : undefined}
                    submitButtonText={canSubmitAssignment || isResubmissionActive ? (isResubmissionActive ? 'Resubmit' : 'Complete') : undefined}
                    onSubmit={handleComponentAssignmentSubmit}
                    customSubmit={!!(canSubmitAssignment || isResubmissionActive)}
                    resubmitSubmissionId={compSubForAssignment?.assignmentsubmission || undefined}
                    submission={(isResubmissionActive || canSubmitAssignment) ? undefined : (fullAssignmentSubmission || (cachedSubmission ? { submissiondata: cachedSubmission.submissiondata, graderesult: cachedSubmission.graderesult } : undefined))}
                  />
                </div>
              )
            ) : (
              <div>
                <h5>{component.title || 'Untitled Assignment'}</h5>
                <p className="no-content">Assignment details not available</p>
              </div>
            )}
          </div>
        );
        
      default:
        return (
          <div className="unknown-component">
            <p>Unknown component type: {component.type}</p>
          </div>
        );
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  // Show loading overlay during completion
  if (completionLoading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '12px',
          textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
        }}>
          <LoadingSpinner />
          <div style={{ marginTop: '1rem', fontSize: '16px', fontWeight: '600', color: '#2c3e50' }}>
            Completing component...
          </div>
        </div>
      </div>
    );
  }

  // Show loading overlay during main post response submission
  if (submittingResponse) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '12px',
          textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
        }}>
          <LoadingSpinner />
          <div style={{ marginTop: '1rem', fontSize: '16px', fontWeight: '600', color: '#2c3e50' }}>
            Submitting response...
          </div>
        </div>
      </div>
    );
  }

  // Show loading overlay during comment reply submission
  if (submittingCommentReply) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '2rem',
          borderRadius: '12px',
          textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
        }}>
          <LoadingSpinner />
          <div style={{ marginTop: '1rem', fontSize: '16px', fontWeight: '600', color: '#2c3e50' }}>
            Submitting reply...
          </div>
        </div>
      </div>
    );
  }

  // Show critical completion error popup
  if (criticalCompletionError) {
    return (
      <CriticalErrorPopup
        isOpen={true}
        title={criticalCompletionError.title}
        message={criticalCompletionError.message}
        details={criticalCompletionError.details}
        showHomeButton={false}
        showReloadButton={criticalCompletionError.showReloadButton}
        reloadButtonText={criticalCompletionError.reloadButtonText}
        onReloadClick={criticalCompletionError.onReloadClick}
      />
    );
  }

  if (error) {
    // Check if it's an authorization error
    if (error.includes('Unauthorized access')) {
      return (
        <CriticalErrorPopup
          isOpen={true}
          title="Unauthorized Access"
          message="This course viewer requires instructor preview access. Please use the Course Builder to view courses."
          details="The Course Viewer is only accessible through the Course Builder with proper preview permissions. Students should not access this page directly."
          showHomeButton={true}
          homeButtonText="Go Home"
          onHomeClick={() => navigate('/')}
        />
      );
    }
    
    // Check if it's an enrollment error
    if (error.includes('No valid enrollment') || error.includes('not enrolled in this course')) {
      return (
        <CriticalErrorPopup
          isOpen={true}
          title="Enrollment Required"
          message="You need a valid enrollment to access this course."
          details="Please contact your instructor or administrator to enroll in this course, or return to your course list."
          showHomeButton={true}
          homeButtonText="Go to My Courses"
          onHomeClick={() => navigate('/my-courses')}
        />
      );
    }
    
    // Other course loading errors
    return (
      <CriticalErrorPopup
        isOpen={true}
        title="Course Loading Error"
        message="Failed to load the requested course. The course may not exist or you may not have permission to access it."
        details={error}
        showHomeButton={true}
        homeButtonText="Go Home"
        onHomeClick={() => navigate('/')}
      />
    );
  }

  if (!course) {
    return (
      <CriticalErrorPopup
        isOpen={true}
        title="Course Not Found"
        message="The requested course could not be found or may have been deleted."
        details="Please check the course ID and try again, or contact your administrator if the problem persists."
        showHomeButton={true}
        homeButtonText="Go Home"
        onHomeClick={() => navigate('/')}
      />
    );
  }

  const components = course.draft?.components || [];

  // Convert components to sections structure (similar to CourseBuilder)
  const buildSectionsFromComponents = (components) => {
    const sections = [];
    const sectionComponents = components.filter(comp => comp.level === 'section');
    
    sectionComponents.forEach(sectionComp => {
      const section = {
        id: sectionComp.id,
        index: sectionComp.sort_index,
        sort_index: sectionComp.sort_index,
        text: sectionComp.title || '',
        content: sectionComp.content || [],
        title: sectionComp.post_title || '',
        type: sectionComp.type,
        subsections: [],
        expanded: true,
        primaryConcentration: sectionComp.concentration || '',
        otherConcentrations: sectionComp.otherconcentrations || [],
        taxonomy: sectionComp.taxonomy || [],
        hourawards: sectionComp.hourawards || [],
        gradeSettings: sectionComp.grade_settings || null,
        status: sectionComp.status || 'Draft',
        reference_table: sectionComp.reference_table,
        reference_id: sectionComp.reference_id,
        createddate: sectionComp.createddate || null,
        updateddate: sectionComp.updateddate || null,
        assignments: sectionComp.type === 'Assignment' && sectionComp.reference_id ? [{ id: sectionComp.reference_id }] : []
      };
      
      // Find subsections for this section
      const subsectionComponents = components.filter(comp => 
        comp.level === 'subsection' && comp.parent === sectionComp.id
      );
      
      subsectionComponents.forEach(subsectionComp => {
        const subsection = {
          id: subsectionComp.id,
          index: subsectionComp.sort_index,
          sort_index: subsectionComp.sort_index,
          text: subsectionComp.title || '',
          content: subsectionComp.content || [],
          title: subsectionComp.post_title || '',
          type: subsectionComp.type,
          nestedSections: [],
          expanded: true,
          primaryConcentration: subsectionComp.concentration || '',
          otherConcentrations: subsectionComp.otherconcentrations || [],
          taxonomy: subsectionComp.taxonomy || [],
          hourawards: subsectionComp.hourawards || [],
          gradeSettings: subsectionComp.grade_settings || null,
          status: subsectionComp.status || 'Draft',
          reference_table: subsectionComp.reference_table,
          reference_id: subsectionComp.reference_id,
          createddate: subsectionComp.createddate || null,
          updateddate: subsectionComp.updateddate || null,
          assignments: subsectionComp.type === 'Assignment' && subsectionComp.reference_id ? [{ id: subsectionComp.reference_id }] : []
        };
        
        // Find nested sections for this subsection
        const nestedComponents = components.filter(comp => 
          comp.level === 'nested' && comp.parent === subsectionComp.id
        );
        
        nestedComponents.forEach(nestedComp => {
          const nestedSection = {
            id: nestedComp.id,
            index: nestedComp.sort_index,
            sort_index: nestedComp.sort_index,
            text: nestedComp.title || '',
            content: nestedComp.content || [],
            title: nestedComp.post_title || '',
            type: nestedComp.type,
            primaryConcentration: nestedComp.concentration || '',
            otherConcentrations: nestedComp.otherconcentrations || [],
            taxonomy: nestedComp.taxonomy || [],
            hourawards: nestedComp.hourawards || [],
            gradeSettings: nestedComp.grade_settings || null,
            status: nestedComp.status || 'Draft',
            reference_table: nestedComp.reference_table,
            reference_id: nestedComp.reference_id,
            createddate: nestedComp.createddate || null,
            updateddate: nestedComp.updateddate || null,
            assignments: nestedComp.type === 'Assignment' && nestedComp.reference_id ? [{ id: nestedComp.reference_id }] : []
          };
          
          subsection.nestedSections.push(nestedSection);
        });
        
        // Sort nested sections by sort_index
        subsection.nestedSections.sort((a, b) => (a.sort_index || 0) - (b.sort_index || 0));
        
        section.subsections.push(subsection);
      });
      
      // Sort subsections by sort_index
      section.subsections.sort((a, b) => (a.sort_index || 0) - (b.sort_index || 0));
      
      sections.push(section);
    });
    
    // Sort sections by sort_index
    sections.sort((a, b) => (a.sort_index || 0) - (b.sort_index || 0));
    
    return sections;
  };

  const sections = buildSectionsFromComponents(components);

  // Helper function to get all components in a flat structure for navigation
  const getAllFlatComponents = () => {
    const flatComponents = [];
    sections.forEach(section => {
      flatComponents.push(section);
      section.subsections?.forEach(subsection => {
        flatComponents.push(subsection);
        subsection.nestedSections?.forEach(nestedSection => {
          flatComponents.push(nestedSection);
        });
      });
    });
    return flatComponents;
  };


  const flatComponents = getAllFlatComponents();
  
  // Filter to only content components for navigation and counting
  const contentComponents = getAllContentComponents(flatComponents);
  
  // Fix initial component selection if it's pointing to a non-content component
  let correctedComponentIndex = currentComponentIndex;
  if (flatComponents[currentComponentIndex] && !isContentComponent(flatComponents[currentComponentIndex])) {
    // Find the first content component in flatComponents
    const firstContentComponent = contentComponents[0];
    if (firstContentComponent) {
      correctedComponentIndex = flatComponents.findIndex(c => c.id === firstContentComponent.id);
    }
  }
  
  const currentComponent = flatComponents[correctedComponentIndex];
  const totalContentComponents = contentComponents.length;

  // Render the course outline using sections structure (based on CourseBuilder)
  const renderCourseOutline = () => {
    return sections.map((section, sectionIndex) => {
      // Check if section has children
      const hasChildren = components.filter(c => c.parent === section.id).length > 0;
      
      return (
      <div key={section.id} className="viewer-outline-section-item">
        <div 
          className={`viewer-outline-section-header ${currentComponent && currentComponent.id === section.id ? 'current-item' : ''} ${isCourseworkMode && isContentComponent(section) && !canAccessComponent(section) ? 'disabled-item' : ''}`}
            onClick={() => {
            // If it's a content component, navigate to it (with access check)
            if (isContentComponent(section)) {
              if (!canAccessComponent(section)) {
                console.log('[CourseViewer] Component not accessible:', section.id);
                return; // Don't navigate if not accessible
              }
              const sectionIndex = flatComponents.findIndex(c => c.id === section.id);
              if (sectionIndex !== -1) {
                setCurrentComponentIndex(sectionIndex);
              }
            } else {
              // If it's a container component, toggle expand/collapse
              toggleSection(section.id);
            }
          }}
          title={
            isContentComponent(section) 
              ? "Click to view this content" 
              : expandedSections.has(section.id) 
                ? "Click to collapse subsections" 
                : "Click to expand subsections"
          }
        >
          {hasChildren && (
            <button 
              className="viewer-outline-expand-button"
              onClick={(e) => {
                e.stopPropagation();
                toggleSection(section.id);
              }}
              title={expandedSections.has(section.id) ? "Collapse subsections" : "Expand subsections"}
            >
              {expandedSections.has(section.id) ? '▼' : '▶'}
            </button>
          )}
          <span className="viewer-outline-section-index">{sectionIndex + 1}.</span>
          <span className="viewer-outline-section-text">
            {section.text || <em>Untitled {section.type}</em>}
            </span>
        </div>
        
        {/* Show subsections if expanded */}
        {expandedSections.has(section.id) && section.subsections && section.subsections.length > 0 && (
          <div className="viewer-outline-subsections-container">
            {section.subsections.map((subsection, subIndex) => {
              // Check if subsection has children
              const subsectionHasChildren = components.filter(c => c.parent === subsection.id).length > 0;
              
              return (
              <div key={subsection.id} className="viewer-outline-subsection-item">
                <div 
                  className={`viewer-outline-subsection-header ${currentComponent && currentComponent.id === subsection.id ? 'current-item' : ''} ${isCourseworkMode && isContentComponent(subsection) && !canAccessComponent(subsection) ? 'disabled-item' : ''}`}
                  onClick={() => {
                    // If it's a content component, navigate to it (with access check)
                    if (isContentComponent(subsection)) {
                      if (!canAccessComponent(subsection)) {
                        console.log('[CourseViewer] Component not accessible:', subsection.id);
                        return; // Don't navigate if not accessible
                      }
                      const subsectionIndex = flatComponents.findIndex(c => c.id === subsection.id);
                      if (subsectionIndex !== -1) {
                        setCurrentComponentIndex(subsectionIndex);
                      }
                    } else {
                      // If it's a container component, toggle expand/collapse
                      toggleSection(subsection.id);
                    }
                  }}
                  title={
                    isContentComponent(subsection) 
                      ? "Click to view this content" 
                      : expandedSections.has(subsection.id) 
                        ? "Click to collapse nested sections" 
                        : "Click to expand nested sections"
                  }
                >
                  {subsectionHasChildren && (
                    <button 
                      className="viewer-outline-expand-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSection(subsection.id);
                      }}
                      title={expandedSections.has(subsection.id) ? "Collapse nested sections" : "Expand nested sections"}
                    >
                      {expandedSections.has(subsection.id) ? '▼' : '▶'}
                    </button>
                  )}
                  <span className="viewer-outline-subsection-index">{sectionIndex + 1}.{subIndex + 1}.</span>
                  <span className="viewer-outline-subsection-text">
                    {subsection.text || <em>Untitled {subsection.type}</em>}
              </span>
          </div>
          
                {/* Show nested sections if expanded */}
                {expandedSections.has(subsection.id) && subsection.nestedSections && subsection.nestedSections.length > 0 && (
                  <div className="viewer-outline-nested-sections-container">
                    {subsection.nestedSections.map((nestedSection, nestedIndex) => (
                      <div key={nestedSection.id} className="viewer-outline-nested-section-item">
                        <div 
                          className={`viewer-outline-nested-section-header ${currentComponent && currentComponent.id === nestedSection.id ? 'current-item' : ''} ${isCourseworkMode && isContentComponent(nestedSection) && !canAccessComponent(nestedSection) ? 'disabled-item' : ''}`}
                          onClick={() => {
                            // If it's a content component, navigate to it (with access check)
                            if (isContentComponent(nestedSection)) {
                              if (!canAccessComponent(nestedSection)) {
                                console.log('[CourseViewer] Component not accessible:', nestedSection.id);
                                return; // Don't navigate if not accessible
                              }
                              const nestedIndex = flatComponents.findIndex(c => c.id === nestedSection.id);
                              if (nestedIndex !== -1) {
                                setCurrentComponentIndex(nestedIndex);
                              }
                            }
                            // Note: Nested sections are typically content components, so no expand/collapse needed
                          }}
                          title={
                            isContentComponent(nestedSection) 
                              ? "Click to view this content" 
                              : "Click to view this content"
                          }
                        >
                          <span className="viewer-outline-nested-section-index">{sectionIndex + 1}.{subIndex + 1}.{nestedIndex + 1}.</span>
                          <span className="viewer-outline-nested-section-text">
                            {nestedSection.text || <em>Untitled {nestedSection.type}</em>}
                          </span>
                        </div>
                      </div>
                    ))}
            </div>
          )}
        </div>
              );
            })}
          </div>
        )}
      </div>
      );
    });
  };

  const handleComponentAssignmentSubmit = async (result) => {
    try {
      if (!isCourseworkMode || !currentComponent || currentComponent.type !== 'Assignment') return;
      if (!validEnrollment || !courseEnrollment) return;

      const token = localStorage.getItem('token');

      // Check if this is a resubmission
      const existing = componentSubmissions[currentComponent.id];
      const isResubmission = !!existing && !!existing.assignmentsubmission;
      const assignment = referencedAssignments[currentComponent.reference_id];
      const isPointsAssignment = assignment && assignment.gradingresulttype === 'Points';
      
      // Resubmission validation
      if (isResubmission) {
        if (!isPointsAssignment) {
          console.error('[CourseViewer] Resubmission not allowed for non-Points assignments');
          alert('Resubmission is only allowed for Points-type assignments.');
          return;
        }
        const allowResubmission = existing.allow_resubmission === true;
        const isSubmitted = existing.status === 'submitted';
        
        // Allow resubmission if:
        // 1. Instructor explicitly allowed it (allow_resubmission === true) - ignore attempts, OR
        // 2. Status is 'submitted' (not yet graded) - ignore attempts, allow until instructor grades
        // Otherwise, if completed and not allowed, block resubmission
        if (existing.status === 'completed' && !allowResubmission) {
          console.error('[CourseViewer] Resubmission not allowed for completed assignments without instructor permission');
          alert('Resubmission is not allowed once the assignment is completed. Please contact your instructor if you need to resubmit.');
          return;
        }
        // No attempt limit checks - students can resubmit if allow_resubmission is true or status is 'submitted'
      }

      // Determine grade settings
      let gradeSettings = currentComponent.gradeSettings;
      if (typeof gradeSettings === 'string') {
        try { gradeSettings = JSON.parse(gradeSettings); } catch { gradeSettings = {}; }
      }
      const minScore = gradeSettings?.criteria?.min_score ?? 70;

      // Determine hoursEarned (when completed)
      let hoursEarned = null;
      if (currentComponent.hourawards) {
        try {
          hoursEarned = typeof currentComponent.hourawards === 'string' ? JSON.parse(currentComponent.hourawards) : currentComponent.hourawards;
        } catch { hoursEarned = null; }
      }

      const gradingResultType = result?.gradingResultType;
      const requiresManual = !!result?.requiresManual;
      const autoGrade = result?.autoGrade;
      const submissionData = result?.submissionData || [];

      // Compute graderesult for assignmentsubmissions
      let graderesult = 'needsEvaluation';
      if (gradingResultType === 'Survey') {
        graderesult = 'not-required';
      } else if (gradingResultType === 'Points') {
        if (requiresManual) {
          graderesult = 'needsEvaluation';
        } else if (autoGrade && autoGrade.maxScore > 0) {
          const pct = (autoGrade.score / autoGrade.maxScore) * 100;
          graderesult = `${pct.toFixed(1)}%`;
        } else {
          graderesult = 'needsEvaluation';
        }
      }

      // Create or update assignmentsubmissions item (custom flow for coursework/components)
      let createdAssignmentSubmissionId = null;
      try {
        if (isResubmission) {
          // Update existing assignmentsubmission
          const existingAssignmentSubmissionId = existing.assignmentsubmission;
          const updatehistory = [
            {
              action: 'resubmitted',
              timestamp: new Date().toISOString(),
              who: `${userData?.first_name || ''} ${userData?.last_name || ''} (${userData?.primary_email || ''})`.trim()
            }
          ];
          const emaildata = {
            studentEmail: userData?.primary_email,
            studentName: `${userData?.first_name || ''} ${userData?.last_name || ''}`.trim(),
            assignmentId: currentComponent.reference_id
          };
          const submitRes = await axios.put(`/api/coursework/assignment-submit/${existingAssignmentSubmissionId}`, {
            updatehistory,
            submissiondata: submissionData,
            graderesult,
            who: `${userData?.first_name || ''} ${userData?.last_name || ''} (${userData?.primary_email || ''})`.trim(),
            emaildata
          }, { headers: { Authorization: `Bearer ${token}` } });
          createdAssignmentSubmissionId = submitRes.data?.assignmentSubmission?.id || existingAssignmentSubmissionId;
          console.log('[CourseViewer] Assignment submission updated for resubmission:', createdAssignmentSubmissionId);
        } else {
          // Create new assignmentsubmission - use the ID from AssignmentViewer (used for upload path) so they match
          const assignmentSubmissionId = result?.assignmentSubmissionId || uuidv4();
          const updatehistory = [
            {
              action: 'submitted',
              timestamp: new Date().toISOString(),
              who: `${userData?.first_name || ''} ${userData?.last_name || ''} (${userData?.primary_email || ''})`.trim()
            }
          ];
          const emaildata = {
            studentEmail: userData?.primary_email,
            studentName: `${userData?.first_name || ''} ${userData?.last_name || ''}`.trim(),
            assignmentId: currentComponent.reference_id
          };
          const submitRes = await axios.post('/api/coursework/assignment-submit', {
            id: assignmentSubmissionId,
            updatehistory,
            enrollment: validEnrollment.enrollment_id,
            submissiondata: submissionData,
            graderesult,
            assignment: currentComponent.reference_id,
            attempt: 1,
            emaildata
          }, { headers: { Authorization: `Bearer ${token}` } });
          createdAssignmentSubmissionId = submitRes.data?.assignmentSubmission?.id || assignmentSubmissionId;
        }
        
        // Cache for immediate display without refetch
        setAssignmentSubmissionByComponent(prev => ({
          ...prev,
          [currentComponent.id]: {
            submissiondata: submissionData,
            graderesult
          }
        }));
        
        // Load the full assignment submission data for proper display
        if (createdAssignmentSubmissionId) {
          loadAssignmentSubmissionData(currentComponent.id, createdAssignmentSubmissionId);
        }
      } catch (e) {
        console.error('[CourseViewer] Unable to create/update assignmentsubmission:', e?.response?.status || e?.message);
        // For new submissions, do NOT create componentsubmission without assignmentsubmission reference
        if (!isResubmission) {
          throw e;
        }
      }

      // Determine status per rules for componentsubmissions
      let status = 'submitted';
      let appliedHours = null;
      if (gradingResultType === 'Survey') {
        status = 'completed';
        appliedHours = hoursEarned;
      } else if (gradingResultType === 'Points') {
        if (requiresManual) {
          status = 'submitted';
          appliedHours = null; // allow move on despite submitted (Assignment-only)
        } else if (autoGrade && autoGrade.maxScore > 0) {
          const pct = (autoGrade.score / autoGrade.maxScore) * 100;
          // Auto-graded assignments are considered completed; hours depend on min_score
          status = 'completed';
          if (pct >= Number(minScore)) {
            appliedHours = hoursEarned;
          } else {
            appliedHours = null; // or 0-equivalent; keep null to indicate none awarded
          }
        } else {
          // No auto-grade info; treat as completed without hours
          status = 'completed';
          appliedHours = null;
        }
      }

      // Avoid duplicate creation; update if exists
      const existingCompSub = componentSubmissions[currentComponent.id];
      
      // Handle hours_earned adjustment for resubmissions
      // If this is a resubmission and hours were previously awarded, adjust based on new score
      if (isResubmission && existingCompSub) {
        const hadHoursAwarded = existingCompSub.hours_earned !== null && existingCompSub.hours_earned !== undefined;
        if (hadHoursAwarded) {
          console.log('[CourseViewer] Resubmission detected with previously awarded hours. Recalculating...');
          console.log('[CourseViewer] Previous hours_earned:', existingCompSub.hours_earned);
          console.log('[CourseViewer] New appliedHours:', appliedHours);
          // appliedHours is already calculated above based on new score
          // If new score doesn't meet min_score, appliedHours will be null (removing previously awarded hours)
        }
      }
      let componentSubmissionId = null;
      
      if (!existingCompSub) {
        // Assignment components must have assignmentsubmission reference - never create without it
        if (!createdAssignmentSubmissionId) {
          throw new Error('Assignment submission must be created before component submission');
        }
        const payload = {
          component: currentComponent.id,
          course_enrollment: courseEnrollment.id,
          enrollment: validEnrollment.enrollment_id,
          accesscard: validEnrollment.accesscard_id,
          account: userData?.account_id,
          status,
          hours_earned: appliedHours,
          completion_data: { completed: status === 'completed' },
          assignmentsubmission: createdAssignmentSubmissionId
        };
        const res = await axios.post('/api/component-submissions', payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        componentSubmissionId = res.data.componentSubmission.id;
        setComponentSubmissions(prev => ({ ...prev, [currentComponent.id]: res.data.componentSubmission }));
      } else {
        // For resubmissions, increment attempt number (tracking only, no limit)
        const currentCompSubAttempt = existingCompSub.attempt || 1;
        const newCompSubAttempt = isResubmission ? (currentCompSubAttempt + 1) : currentCompSubAttempt;
        
        // For resubmissions, always update assignmentsubmission reference (even if it's the same ID)
        const updatePayload = {
          status,
          hours_earned: appliedHours, // This will adjust hours if score changed
          completion_data: { completed: status === 'completed' },
          attempt: newCompSubAttempt, // Increment attempt for resubmissions (tracking only, no limit)
          // Keep the assignmentsubmission reference (it's being updated, not replaced)
          assignmentsubmission: existingCompSub.assignmentsubmission || createdAssignmentSubmissionId || null
        };
        const res = await axios.put(`/api/component-submissions/${existingCompSub.id}`, updatePayload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        componentSubmissionId = res.data.componentSubmission.id;
        setComponentSubmissions(prev => ({ ...prev, [currentComponent.id]: res.data.componentSubmission }));
        
        // Load the full assignment submission data for proper display
        if (createdAssignmentSubmissionId) {
          loadAssignmentSubmissionData(currentComponent.id, createdAssignmentSubmissionId);
        }
      }

      // Remove from active resubmissions if this was a resubmission
      if (isResubmission) {
        handleCancelResubmission(currentComponent.id);
      }

      // Refresh UI messaging/tooling
      window.dispatchEvent(new CustomEvent('refreshDiscussionResponses'));

      // Scroll to top to reveal the completed/submitted banner
      setTimeout(() => {
        try {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch {}
      }, 150);
    } catch (e) {
      console.error('[CourseViewer] Error handling component assignment submit:', e);
      alert('Failed to finalize component submission. Please try again.');
    }
  };

  return (
    <div className="course-viewer">
      {/* Preview Mode Notice - Similar to AssignmentViewer */}
      {isPreviewMode && (
        <div style={{
          maxWidth: 1200,
          margin: '20px auto 0 auto',
          padding: 16,
          backgroundColor: '#ffebee',
          borderRadius: 8,
          border: '1px solid #f44336',
          borderLeft: '4px solid #f44336'
        }}>
          <div style={{ fontWeight: 600, color: '#c62828', marginBottom: 4 }}>
            DO NOT SHARE THIS LINK WITH STUDENTS
          </div>
          <div style={{ fontSize: 14, color: '#d32f2f' }}>
            This course is being displayed in preview mode for instructor review.
          </div>
        </div>
      )}
      
      <div className="course-viewer-header">
        <h1 className="course-title">{course.title || 'Untitled Course'}</h1>
      </div>

      <div className="course-viewer-content">
        {/* Left Sidebar */}
        <div className={`course-sidebar ${expandedSections.has('sidebar-collapsed') ? 'collapsed' : ''}`}>
          <div className="sidebar-header">
            <div className="sidebar-header-content">
              <h3>Course Outline</h3>
              <span className="component-count">{totalContentComponents} Items</span>
            </div>
            <button 
              className="sidebar-toggle-button"
              onClick={() => toggleSection('sidebar-collapsed')}
              title={expandedSections.has('sidebar-collapsed') ? 'Expand Sidebar' : 'Collapse Sidebar'}
            >
              {expandedSections.has('sidebar-collapsed') ? '◀' : '▶'}
            </button>
          </div>
          
                               <div className="sidebar-content">
            {sections.length > 0 ? (
              renderCourseOutline()
            ) : (
              <div className="no-components-message">
                <p>No sections found for this course.</p>
              </div>
            )}
          </div>
        </div>

        {/* Main Content Area */}
        <div className="course-main-content">
          {currentComponent ? (
            <>
              <div className="course-viewer-content-header">
                <div className="course-viewer-navigation">
                  <button 
                    className="course-viewer-nav-button course-viewer-prev-button"
                    onClick={goToPrevious}
                    disabled={getPreviousContentIndex(currentComponentIndex) === currentComponentIndex}
                  >
                    ← Previous
                  </button>
                  
                  <span className="course-viewer-counter">
                    {(() => {
                      const currentContentIndex = contentComponents.findIndex(c => c.id === currentComponent.id);
                      return `${currentContentIndex + 1} of ${totalContentComponents}`;
                    })()}
                  </span>
                  
                  <button 
                    className="course-viewer-nav-button course-viewer-next-button"
                    onClick={goToNext}
                    disabled={getNextContentIndex(currentComponentIndex) === currentComponentIndex || !isCurrentComponentCompleted()}
                    title={
                      isCourseworkMode && !isCurrentComponentCompleted() 
                        ? "Complete this component to proceed to the next one"
                        : getNextContentIndex(currentComponentIndex) === currentComponentIndex
                        ? "No more components"
                        : "Go to next component"
                    }
                  >
                    Next →
                  </button>
                </div>
                
                <div className="course-viewer-component-info">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                    <div>
                      <h2 className="course-viewer-title">{currentComponent.text || `Untitled ${currentComponent.type}`}</h2>
                      <span className="course-viewer-type">{currentComponent.type}</span>
                    </div>
                    {/* Comments Button - Only show if there's a component submission */}
                    {shouldShowComments() && (
                      <button
                        style={{
                          padding: 0,
                          background: 'none',
                          border: '2px solid #1D4E89',
                          borderRadius: 8,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'transform 0.2s ease',
                          marginLeft: '16px'
                        }}
                        onClick={() => setShowCommentsModal(true)}
                        onMouseOver={e => e.currentTarget.style.transform = 'scale(1.05)'}
                        onMouseOut={e => e.currentTarget.style.transform = 'scale(1)'}
                        title="View Comments"
                      >
                        <svg width="40" height="40" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect width="48" height="48" rx="12" fill="#F5F7FA"/>
                          <path d="M12 16C12 13.7909 13.7909 12 16 12H32C34.2091 12 36 13.7909 36 16V26C36 28.2091 34.2091 30 32 30H22L16 36V30H16C13.7909 30 12 28.2091 12 26V16Z" fill="white" stroke="#4A5568" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="18" cy="21" r="1.5" fill="#4A5568"/>
                          <circle cx="24" cy="21" r="1.5" fill="#4A5568"/>
                          <circle cx="30" cy="21" r="1.5" fill="#4A5568"/>
                        </svg>
                      </button>
                    )}
                  </div>
                  
                    {/* Grade Settings Display - Subtle */}
                    {(() => {
                      if (!isCourseworkMode || !currentComponent.gradeSettings) return null;
                      
                      // Don't show instruction if component is already completed
                      const isCompleted = isComponentCompleted(currentComponent.id);
                      console.log('[CourseViewer] Rendering instruction text - Current component:', currentComponent);
                      console.log('[CourseViewer] Rendering instruction text - Is completed:', isCompleted);
                      if (isCompleted) return null;
                      
                      try {
                        const gradeSettings = typeof currentComponent.gradeSettings === 'string' 
                          ? JSON.parse(currentComponent.gradeSettings) 
                          : currentComponent.gradeSettings;
                        
                        // Content type completion
                        if (currentComponent.type === 'Content' && gradeSettings.type === 'completion' && gradeSettings.criteria === 'click_complete') {
                          return (
                            <div style={{
                              fontSize: '12px',
                              color: '#666',
                              fontStyle: 'italic',
                              marginTop: '4px'
                            }}>
                              Click complete to continue
                            </div>
                          );
                        }

                        // Assignment type instruction
                        if (currentComponent.type === 'Assignment') {
                          const compSub = componentSubmissions[currentComponent.id];
                          if (compSub && compSub.status === 'completed') {
                            return null;
                          }
                          if (compSub && compSub.status === 'submitted') {
                            return (
                              <div style={{
                                fontSize: '12px',
                                color: '#e53935',
                                fontStyle: 'italic',
                                marginTop: '4px',
                                fontWeight: '500'
                              }}>
                                Waiting for instructor to grade, but you may move on
                              </div>
                            );
                          }
                          return (
                            <div style={{
                              fontSize: '12px',
                              color: '#e53935',
                              fontStyle: 'italic',
                              marginTop: '4px',
                              fontWeight: '500'
                            }}>
                              Complete the assignment below
                            </div>
                          );
                        }
                        
                        // Discussion Board type completion
                        if (currentComponent.type === 'Discussion Board' && gradeSettings.type === 'discussion_board') {
                          const criteriaType = gradeSettings.criteria?.type;
                          let instructionText = '';
                          
                          switch (criteriaType) {
                            case 'respond_to_main':
                              instructionText = 'Respond to the main post to continue';
                              break;
                            case 'respond_to_main_and_students':
                              const minResponses = gradeSettings.criteria?.min_responses || 1;
                              instructionText = `Respond to the main post and to ${minResponses} students to continue`;
                              break;
                            /* removing these for now for simplicity
                            case 'manual_after_main':
                              instructionText = 'Respond to the main post - instructor will grade manually';
                              break;
                            case 'manual_after_main_and_students':
                              const minResponsesManual = gradeSettings.criteria?.min_responses || 2;
                              instructionText = `Respond to main post and ${minResponsesManual} students - instructor will grade manually`;
                              break;
                            */
                            default:
                              instructionText = 'Participate in the discussion to continue';
                          }
                          
                          return (
                            <div style={{
                              fontSize: '12px',
                              color: '#e53935',
                              fontStyle: 'italic',
                              marginTop: '4px',
                              fontWeight: '500'
                            }}>
                              {instructionText}
                            </div>
                          );
                        }
                      } catch (e) {
                        console.error('[CourseViewer] Error parsing grade settings:', e);
                      }
                      return null;
                    })()}
                </div>
              </div>
              
              <div className="content-body">
                {/* Pending/Completed Banner - Show at top based on status */}
                {isCourseworkMode && currentComponent && (() => {
                  const submission = componentSubmissions[currentComponent.id];
                  if (!submission) return null;

                  // Special pending banner for Assignment submissions awaiting grading
                  if (currentComponent.type === 'Assignment' && submission.status === 'submitted') {
                    return (
                      <div style={{
                        padding: '1rem',
                        backgroundColor: '#fff3cd',
                        border: '1px solid #ffeeba',
                        borderRadius: '8px',
                        color: '#856404',
                        fontWeight: '600',
                        marginBottom: '1.5rem'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span>⏳ Your hours are pending — instructor must grade this assignment. You may move on.</span>
                        </div>
                      </div>
                    );
                  }

                  // Special pending banner for Discussion Board submissions awaiting completion
                  if (currentComponent.type === 'Discussion Board' && submission.status === 'submitted') {
                    // Get hours that can be earned
                    let hoursText = '';
                    if (currentComponent.hourawards) {
                      try {
                        const hoursEarned = typeof currentComponent.hourawards === 'string' 
                          ? JSON.parse(currentComponent.hourawards) 
                          : currentComponent.hourawards;
                        
                        if (hoursEarned && Array.isArray(hoursEarned) && hoursEarned.length > 0) {
                          hoursText = hoursEarned.map(award => `${award.hours} ${award.type}`).join(', ');
                        } else if (hoursEarned && typeof hoursEarned === 'object') {
                          hoursText = Object.entries(hoursEarned)
                            .map(([type, hours]) => `${hours} ${type}`)
                            .join(', ');
                        }
                      } catch (e) {
                        console.error('[CourseViewer] Error parsing hourawards:', e);
                      }
                    }

                    // Get instruction text from grade settings
                    let instructionText = '';
                    try {
                      const gradeSettings = typeof currentComponent.gradeSettings === 'string' 
                        ? JSON.parse(currentComponent.gradeSettings) 
                        : currentComponent.gradeSettings;
                      
                      if (gradeSettings && gradeSettings.type === 'discussion_board') {
                        const criteriaType = gradeSettings.criteria?.type;
                        
                        switch (criteriaType) {
                          case 'respond_to_main':
                            instructionText = 'respond to the main post';
                            break;
                          case 'respond_to_main_and_students':
                            const minResponses = gradeSettings.criteria?.min_responses || 1;
                            instructionText = `respond to the main post and to ${minResponses} students`;
                            break;
                          default:
                            instructionText = 'participate in the discussion';
                        }
                      }
                    } catch (e) {
                      console.error('[CourseViewer] Error parsing grade settings:', e);
                      instructionText = 'complete the discussion requirements';
                    }

                    return (
                      <div style={{
                        padding: '1rem',
                        backgroundColor: '#fff3cd',
                        border: '1px solid #ffeeba',
                        borderRadius: '8px',
                        color: '#856404',
                        fontWeight: '600',
                        marginBottom: '1.5rem'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span>
                            ⏳ You may move on, but to get credit {hoursText ? `(${hoursText} hours)` : ''} you must {instructionText}.
                          </span>
                        </div>
                      </div>
                    );
                  }

                  // Completed banner for any completed component
                  if (isComponentCompleted(currentComponent.id)) {
                    return (
                      <div style={{
                        padding: '1rem',
                        backgroundColor: '#d4edda',
                        border: '1px solid #c3e6cb',
                        borderRadius: '8px',
                        color: '#155724',
                        fontWeight: '600',
                        marginBottom: '1.5rem'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          <span>✅ Completed</span>
                        </div>
                        {(() => {
                          const submission = componentSubmissions[currentComponent.id];
                          console.log('[CourseViewer] Rendering completed banner - Current component:', currentComponent);
                          console.log('[CourseViewer] Rendering completed banner - Component submission:', submission);
                          
                          // Query comments with reference_id == currentComponent.reference_id
                          if (currentComponent && currentComponent.reference_id &&currentComponent.type === 'Discussion Board') {
                            axios.get(`/api/comments?reference_id=${currentComponent.reference_id}&table=discussionboard`, {
                              headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                            }).then(response => {
                              console.log('[CourseViewer] Comments for reference_id:', currentComponent.reference_id, response.data);
                              
                              // Filter to user's responses to main post
                              const userResponsesToMainPost = response.data.comments.filter((c) => 
                                c.account === userData.account_id && (c.reference_id === c.parent)
                              );
                              console.log('[CourseViewer] User responses to main post:', userResponsesToMainPost);
                            }).catch(error => {
                              console.error('[CourseViewer] Error loading comments for reference_id:', currentComponent.reference_id, error);
                            });
                          }
                          if (submission && submission.hours_earned) {
                            try {
                              const hoursEarned = typeof submission.hours_earned === 'string' 
                                ? JSON.parse(submission.hours_earned) 
                                : submission.hours_earned;
                              
                              if (hoursEarned && Array.isArray(hoursEarned) && hoursEarned.length > 0) {
                                return (
                                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#0d4a2c' }}>
                                    <div style={{ marginBottom: '4px' }}>Hours Earned:</div>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                      {hoursEarned.map((award, index) => (
                                        <span key={index} style={{
                                          backgroundColor: '#c3e6cb',
                                          padding: '2px 8px',
                                          borderRadius: '4px',
                                          fontSize: '13px'
                                        }}>
                                          {award.type}: {award.hours}h
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                );
                              } else if (hoursEarned && typeof hoursEarned === 'object') {
                                // Fallback for object format
                                const hoursText = Object.entries(hoursEarned)
                                  .map(([type, hours]) => `${type}: ${hours}h`)
                                  .join(', ');
                                return (
                                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#0d4a2c' }}>
                                    Hours Earned: {hoursText}
                                  </div>
                                );
                              }
                            } catch (e) {
                              console.error('[CourseViewer] Error parsing hours_earned:', e);
                            }
                          }
                          return null;
                        })()}
                      </div>
                    );
                  }
                  return null;
                })()}
                
                {renderComponentContent(currentComponent)}
              </div>
            </>
          ) : (
            <div className="no-components">
              <h2>No Components Available</h2>
              <p>This course doesn't have any components to display.</p>
            </div>
          )}
        </div>
      </div>
      
      {/* Comments Modal */}
      {shouldShowComments() && (
        <ProjectComments
          referenceId={componentSubmissions[currentComponent.id]?.id}
          table="componentsubmissions"
          userData={userData}
          isOpen={showCommentsModal}
          onClose={() => setShowCommentsModal(false)}
          relevantRole={userData?.accesscards?.find(ac => ac.accesscard_id === userData.default_accesscard_id)?.role === 'Student' ? 'Student' : 'Instructor'}
          onEvaluationComment={() => {
            setShowCommentsModal(false);
            // Optionally refresh component submission data if needed
          }}
        />
      )}
    </div>
  );
};

export default CourseViewer;
